/**
 * AIApiSettings.jsx
 * Centralized KI & API Settings Component
 * Features: AI providers, Complete Chatbot config, Nutrition, SMTP, WhatsApp API, Payments
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import axios from 'axios';
import { AdminAISettingsCard } from './AdminAISettingsCard';
import { ChurnRiskSettingsCard } from './ChurnRiskSettingsCard';
import { AIOptimizerCard } from './AIOptimizerCard';
import { ReferralSettingsCard } from './ReferralSettingsCard';
import { FriendshipTreeSettingsCard } from './FriendshipTreeSettingsCard';
import { ReferralOverviewCard } from './ReferralOverviewCard';
import FeedbackRecipientsPanel from './FeedbackRecipientsPanel';
import ReviewAutomationCard from './ReviewAutomationCard';
import { 
  Sparkles, Bot, Smartphone, CreditCard, Save, RefreshCw, 
  Check, Key, Globe, MessageCircle, Apple, Camera,
  ArrowRight, Shield, Copy, ExternalLink, Mail,
  Upload, Trash2, FileText, Link, Database, Calendar, User,
  Play, TestTube, BarChart3, Trophy, Heart, Watch, AlertCircle, Send, Bell, Info, Loader2,
  Download, Upload as UploadIcon, Leaf, SlidersHorizontal, Mic
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Provider Options
const AI_PROVIDERS = [
  { id: 'openai', name: 'OpenAI (GPT)', description: 'GPT-5.6 Terra/Luna/Sol, Vision', placeholder: 'sk-...' },
  { id: 'anthropic', name: 'Anthropic (Claude)', description: 'Claude Sonnet 4.5, Haiku 4.5', placeholder: 'sk-ant-...' },
  { id: 'google', name: 'Google (Gemini)', description: 'Gemini 2.5 Flash/Pro', placeholder: 'AIza...' },
];

const OPENAI_MODELS = [
  { id: 'gpt-5.6-terra', name: 'GPT-5.6 Terra (Empfohlen – stark & schnell)', recommended: true },
  { id: 'gpt-5.6-luna', name: 'GPT-5.6 Luna (Schnellstes & Günstigstes)' },
  { id: 'gpt-5.6-sol', name: 'GPT-5.6 Sol (Flaggschiff – teurer)' },
  { id: 'gpt-4o', name: 'GPT-4o (Legacy)' },
  { id: 'gpt-4o-mini', name: 'GPT-4o Mini (Legacy)' },
];

const AI_APPLICATION_BLOCKS = [
  {
    id: 'nutrition',
    title: 'Ernährung',
    apps: [
      { label: 'Ernährungs-KI (Text/Analyse)', serviceId: 'nutrition_ai' },
      { label: 'Foto-Analyse (Mahlzeit/Produktfotos)', serviceId: 'photo_analysis' },
      { label: 'Sprach-Analyse Ernährung (nach Whisper-Transkript)', serviceId: 'voice_analysis' },
      { label: 'AI-Quick-Create (Lebensmittel)', serviceId: 'nutrition_quick_create' },
      { label: 'Trainingaktivität-Suche', serviceId: 'training_assistant' },
      { label: 'Mahlzeit-Assistent', serviceId: 'meal_assistant' },
      { label: 'Wochenplan-Generator', serviceId: 'weekly_plan' },
      { label: 'KI-Validierung (Ernährungsziele/Nährwerte)', serviceId: 'validation_ai' },
      { label: 'Produkt-Score (Yuka-ähnlich)', serviceId: null, note: 'Regelbasiert – kein KI-Modell notwendig' },
    ],
  },
  {
    id: 'chat',
    title: 'Kommunikation',
    apps: [
      { label: 'Chatbot', serviceId: 'chatbot' },
      { label: 'Website Widget Chat', serviceId: 'widget_chat' },
    ],
  },
  {
    id: 'mobile',
    title: 'Mobile & Zusatz',
    apps: [
      { label: 'Mobile App KI', serviceId: 'mobile_ai' },
      { label: 'Rezept-KI', serviceId: 'recipe_ai' },
    ],
  },
];

// Phase 3: Aufteilung "KI & API" -> KI / Kanäle / Zahlungen
const TAB_GROUPS = {
  ki: ['ki-allgemein', 'ki-modelle', 'ki-automatik', 'marketing', 'kommunikation', 'ki-ernährung'],
  kanaele: ['email-smtp', 'whatsapp-api', 'firebase'],
  zahlungen: ['zahlungen'],
};
const GROUP_META = {
  ki: { title: 'KI & API', description: 'Provider & Modelle · KI & Automatik · Marketing · Kommunikation · Ernährungs-KI' },
  kanaele: { title: 'Kanäle', description: 'E-Mail (SMTP), WhatsApp API & Push (Firebase)' },
  zahlungen: { title: 'Zahlungen', description: 'Payrexx Zahlungs-Integration' },
};

export const AIApiSettings = ({ group = null }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialSubtab = searchParams.get('subtab');
  const subtabToTab = { chatbot: 'kommunikation', kommunikation: 'kommunikation', marketing: 'marketing', automatik: 'ki-automatik', ernährung: 'ki-ernährung', modelle: 'ki-modelle', smtp: 'email-smtp', whatsapp: 'whatsapp-api' };
  const visibleTabs = group ? (TAB_GROUPS[group] || null) : null;
  const isTabVisible = (t) => !visibleTabs || visibleTabs.includes(t);
  const resolveInitialTab = () => {
    const fromSub = subtabToTab[initialSubtab];
    if (visibleTabs) {
      if (fromSub && visibleTabs.includes(fromSub)) return fromSub;
      return visibleTabs[0];
    }
    return fromSub || 'ki-allgemein';
  };
  const [activeTab, setActiveTab] = useState(resolveInitialTab());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  // 08/2026: Manuelles Wissen + Foto-Extraktion
  const [manualKnowledgeTitle, setManualKnowledgeTitle] = useState('');
  const [manualKnowledgeContent, setManualKnowledgeContent] = useState('');
  const [savingManualKnowledge, setSavingManualKnowledge] = useState(false);
  const [documentTopic, setDocumentTopic] = useState('');
  const [photoExtractPreview, setPhotoExtractPreview] = useState(null);
  const fileInputRef = useRef(null);
  const knowledgeImportRef = useRef(null);
  const configImportRef = useRef(null);
  
  // AI Settings State (KI Provider)
  const [aiSettings, setAiSettings] = useState({
    use_emergent_key: true,
    primary_provider: 'emergent',
    emergent_key: '',
    openai_key: '',
    anthropic_key: '',
    google_key: '',
    serpapi_key: '',
    refdata_api_key: '',
    fallback_enabled: true,
    fallback_provider: 'openai',
    use_emergent_fallback: false,
    fallback_openai_key: '',
    fallback_anthropic_key: '',
    fallback_google_key: '',
    nutrition_ai_enabled: true,
    nutrition_quick_create_enabled: true,
    nutrition_photo_analysis: true,
    nutrition_voice_speed_profile: 'balanced',
    training_activity_resolution_speed_profile: 'fast',
    mobile_ai_enabled: true,
    product_scoring_enabled: true,
    product_scoring_nutrition_enabled: true,
    product_scoring_additives_enabled: true,
    product_scoring_bio_enabled: true,
    product_scoring_weight_nutrition: 60,
    product_scoring_weight_additives: 30,
    product_scoring_weight_bio: 10,
    product_scoring_bio_unknown_policy: 'only_when_known',
    product_scoring_show_search: true,
    product_scoring_show_barcode: true,
    product_scoring_show_product_detail: true,
    product_scoring_show_favorites_recent: true,
  });
  
  // Chatbot Settings State (Complete)
  const [chatbotSettings, setChatbotSettings] = useState({
    enabled: true,
    name: '',
    avatar_url: '',
    provider: 'openai',
    model: 'gpt-5.2',
    use_emergent_key: true,
    custom_api_key: '',
    role_description: '',
    style_description: '',
    boundaries: '',
    knowledge_urls: [],
    knowledge_content: '',
    website_base_url: '',
    booking_enabled: true,
    booking_advance_hours: 2,
    booking_categories_existing_customer: [],
    booking_categories_new_customer: [],
  });
  const [chatbotDocuments, setChatbotDocuments] = useState([]);
  const [newKnowledgeUrl, setNewKnowledgeUrl] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [testResponse, setTestResponse] = useState('');
  const [testingChatbot, setTestingChatbot] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Per-Service AI Model Settings (NEW)
  const [serviceModels, setServiceModels] = useState({});
  const [availableModels, setAvailableModels] = useState({});
  const [aiServices, setAiServices] = useState([]);
  const [savingServiceModels, setSavingServiceModels] = useState(false);
  
  // Payment Settings State (nur Payrexx)
  const [paymentSettings, setPaymentSettings] = useState({
    payrexx_instance_name: '',
    payrexx_api_secret: '',
    payrexx_webhook_secret: '',
  });
  const [webhookTokenConfigured, setWebhookTokenConfigured] = useState(null);
  const [testingPayrexx, setTestingPayrexx] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  
  // SMTP Settings State
  const [smtpSettings, setSmtpSettings] = useState({
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: '',
    enabled: false
  });
  
  // WhatsApp API Settings State
  const [whatsappSettings, setWhatsappSettings] = useState({
    enabled: false,
    phone_id: '',
    access_token: '',
    business_account_id: '',
    verify_token: '',
    meta_app_id: '',
    ai_auto_reply_enabled: false,
    voice_transcription_enabled: true,
    response_speed_profile: 'balanced',
    voice_processing_quick_ack_enabled: true
  });
  
  // Mobile App Settings State
  const [mobileAppSettings, setMobileAppSettings] = useState({
    app_name: '',
    app_slogan: 'Dein Personal Trainer',
    primary_color: '#EAB308',
    accent_color: '#10B981',
    push_appointment_reminders: true,
    push_nutrition_reminders: true,
    push_training_motivation: false,
    push_water_reminders: false,
    push_progress_updates: true,
    feature_photo_analysis: true,
    feature_barcode_scanner: true,
    feature_ai_coaching: true,
    feature_gamification: true,
    feature_health_kit_sync: false,
    feature_smartwatch_support: false,
  });
  const [savingMobileApp, setSavingMobileApp] = useState(false);
  
  // Firebase Settings State
  const [firebaseSettings, setFirebaseSettings] = useState({
    configured: false,
    project_id: '',
    client_email: '',
    service_account_json: '',
  });
  const [savingFirebase, setSavingFirebase] = useState(false);
  const [testingFirebase, setTestingFirebase] = useState(false);
  const [firebaseTestResult, setFirebaseTestResult] = useState(null);
  
  // Nutrition AI Bot Personality Settings
  const [nutritionBotSettings, setNutritionBotSettings] = useState({
    enabled: true,
    name: 'KI-Ernährungsberater',
    role_description: '',
    style_description: '',
    boundaries: '',
    chat_history_hours: 24,
    max_messages_per_session: 50
  });
  const [savingNutritionBot, setSavingNutritionBot] = useState(false);

  const getServiceMeta = (serviceId) => aiServices.find((s) => s.id === serviceId);
  const getServiceConfig = (serviceId) => {
    const service = getServiceMeta(serviceId);
    const fallback = {
      provider: service?.recommended_provider || 'openai',
      model: service?.recommended_model || 'gpt-4o-mini',
      enabled: true,
    };
    return serviceModels[serviceId] || fallback;
  };
  const scrollToServiceCard = (serviceId) => {
    const element = document.getElementById(`ai-service-card-${serviceId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };
  const openModelService = (serviceId) => {
    setActiveTab('ki-modelle');
    window.setTimeout(() => scrollToServiceCard(serviceId), 120);
  };
  
  useEffect(() => {
    loadAllSettings();
    loadServiceModels();
  }, []);
  
  const loadServiceModels = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/settings/ai/service-models`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setServiceModels(response.data.service_models || {});
        setAvailableModels(response.data.available_models || {});
        setAiServices(response.data.services || []);
      }
    } catch (error) {
      console.error('Error loading service models:', error);
    }
  };
  
  const saveServiceModels = async () => {
    setSavingServiceModels(true);
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/settings/ai/service-models`, serviceModels, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('KI-Modelle pro Dienst gespeichert!');
    } catch (error) {
      console.error('Error saving service models:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingServiceModels(false);
    }
  };
  
  const loadAllSettings = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      // Load all settings in parallel
      const [aiRes, chatbotRes, docsRes, generalSettingsRes, smtpRes, waRes, mobileRes, firebaseRes, testStatusesRes, nutritionBotRes] = await Promise.allSettled([
        axios.get(`${API}/settings/ai`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/chatbot/settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/chatbot/documents`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/settings`, { headers: { Authorization: `Bearer ${token}` } }), // General settings for Payrexx
        axios.get(`${API}/email/settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/whatsapp/settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/mobile-app/settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/firebase/settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/settings/ai/test-statuses`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/portal/ai-assistant/settings`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      
      // AI Settings
      if (aiRes.status === 'fulfilled' && aiRes.value.data) {
        const data = { ...aiRes.value.data };
        if (data.default_provider && !data.primary_provider) {
          data.primary_provider = data.default_provider;
        }
        data.use_emergent_key = data.primary_provider === 'emergent' || !data.primary_provider;
        setAiSettings(prev => ({ ...prev, ...data }));
      }
      
      // Load saved API test statuses
      if (testStatusesRes.status === 'fulfilled' && testStatusesRes.value.data) {
        const savedStatuses = testStatusesRes.value.data;
        if (savedStatuses.emergent || savedStatuses.openai || savedStatuses.anthropic || savedStatuses.google || savedStatuses.serpapi) {
          setProviderTestStatuses(prev => ({
            ...prev,
            ...savedStatuses
          }));
        }
      }
      
      // Chatbot Settings - THIS IS THE KEY FIX
      if (chatbotRes.status === 'fulfilled' && chatbotRes.value.data) {
        const chatData = chatbotRes.value.data;
        console.log('Loaded chatbot settings:', chatData);
        setChatbotSettings({
          enabled: chatData.enabled ?? true,
          name: chatData.name || '',
          avatar_url: chatData.avatar_url || '',
          provider: chatData.provider || 'openai',
          model: chatData.model || 'gpt-5.2',
          use_emergent_key: chatData.use_emergent_key ?? true,
          custom_api_key: chatData.custom_api_key || '',
          role_description: chatData.role_description || '',
          style_description: chatData.style_description || '',
          boundaries: chatData.boundaries || '',
          knowledge_urls: chatData.knowledge_urls || [],
          knowledge_content: chatData.knowledge_content || '',
          website_base_url: chatData.website_base_url || '',
          booking_enabled: chatData.booking_enabled ?? true,
          booking_advance_hours: chatData.booking_advance_hours || 2,
          booking_categories_existing_customer: chatData.booking_categories_existing_customer || [],
          booking_categories_new_customer: chatData.booking_categories_new_customer || [],
        });
      }
      
      // Chatbot Documents - handle both formats
      if (docsRes.status === 'fulfilled' && docsRes.value.data) {
        const docsData = docsRes.value.data;
        // Handle if response is { documents: [...] } or direct array
        const docs = Array.isArray(docsData) ? docsData : (docsData.documents || []);
        setChatbotDocuments(docs);
      }
      
      // Payment Settings (Payrexx from general settings)
      if (generalSettingsRes.status === 'fulfilled' && generalSettingsRes.value.data) {
        const settings = generalSettingsRes.value.data;
        setPaymentSettings(prev => ({ 
          ...prev, 
          payrexx_instance_name: settings.payrexx_instance_name || '',
          payrexx_api_secret: settings.payrexx_api_secret || '',
          payrexx_webhook_secret: settings.payrexx_webhook_secret || ''
        }));
      }
      // Payrexx Webhook-Token-Status (Server-Env oder CRM/Studio-Secret)
      try {
        const stRes = await axios.get(`${API}/payrexx/status`, { headers: { Authorization: `Bearer ${token}` } });
        setWebhookTokenConfigured(!!stRes.data?.webhook_token_configured);
      } catch (e) {
        setWebhookTokenConfigured(null);
      }
      
      // SMTP Settings
      if (smtpRes.status === 'fulfilled' && smtpRes.value.data) {
        setSmtpSettings(prev => ({ ...prev, ...smtpRes.value.data }));
      }
      
      // WhatsApp Settings
      if (waRes.status === 'fulfilled' && waRes.value.data) {
        const waData = waRes.value.data;
        setWhatsappSettings(prev => ({ 
          ...prev, 
          ...waData,
          access_token: waData.access_token === '***SET***' ? '' : waData.access_token,
        }));
      }
      
      // Mobile App Settings
      if (mobileRes.status === 'fulfilled' && mobileRes.value.data) {
        setMobileAppSettings(prev => ({ ...prev, ...mobileRes.value.data }));
      }
      
      // Firebase Settings
      if (firebaseRes.status === 'fulfilled' && firebaseRes.value.data) {
        setFirebaseSettings(prev => ({ ...prev, ...firebaseRes.value.data }));
      }
      
      // Nutrition Bot Settings
      if (nutritionBotRes.status === 'fulfilled' && nutritionBotRes.value.data) {
        setNutritionBotSettings(prev => ({ ...prev, ...nutritionBotRes.value.data }));
      }
      
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleTestPayrexx = async () => {
    setTestingPayrexx(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API}/payrexx/test-connection`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (data?.success) {
        toast.success(`Payrexx-Verbindung OK${data.instance_name ? ` (${data.instance_name})` : ''}`);
      } else if (data?.mock_mode) {
        toast.error('Payrexx ist noch nicht konfiguriert — bitte Instance Name + API Secret speichern.');
      } else {
        toast.error(`Payrexx-Test fehlgeschlagen: ${data?.error || 'unbekannt'}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payrexx-Test fehlgeschlagen');
    } finally {
      setTestingPayrexx(false);
    }
  };

  const handleTestSmtp = async () => {
    setTestingSmtp(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API}/settings/smtp/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(data?.message || 'Test-Mail gesendet ✅');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test-Mail fehlgeschlagen');
    } finally {
      setTestingSmtp(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Prepare AI settings
      const aiData = { ...aiSettings };
      if (aiSettings.use_emergent_key) {
        aiData.primary_provider = 'emergent';
      }
      
      // Save all settings — robust (07/2026): Ein Fehler in einem Bereich
      // darf die anderen (v.a. Payrexx) NICHT mehr blockieren.
      const partResults = await Promise.allSettled([
        axios.put(`${API}/settings/ai`, aiData, { headers: { Authorization: `Bearer ${token}` } }),
        axios.put(`${API}/chatbot/settings`, chatbotSettings, { headers: { Authorization: `Bearer ${token}` } }),
        axios.put(`${API}/email/settings`, smtpSettings, { headers: { Authorization: `Bearer ${token}` } }),
        axios.put(`${API}/settings`, {
          payrexx_instance_name: paymentSettings.payrexx_instance_name,
          payrexx_api_secret: paymentSettings.payrexx_api_secret,
          payrexx_webhook_secret: paymentSettings.payrexx_webhook_secret
        }, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const partNames = ['KI', 'Chatbot', 'E-Mail (SMTP)', 'Payrexx'];
      const failedParts = partResults
        .map((r, i) => (r.status === 'rejected' ? partNames[i] : null))
        .filter(Boolean);
      if (failedParts.length > 0) {
        partResults.forEach((r, i) => {
          if (r.status === 'rejected') console.error(`Speichern fehlgeschlagen (${partNames[i]}):`, r.reason);
        });
        toast.error(`Fehler beim Speichern: ${failedParts.join(', ')} — restliche Bereiche wurden gespeichert`);
      }
      
      // Save WhatsApp settings separately (different format)
      if (whatsappSettings.phone_id) {
        await axios.put(`${API}/whatsapp/settings`, null, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            phone_id: whatsappSettings.phone_id,
            access_token: whatsappSettings.access_token || '***SET***',
            business_account_id: whatsappSettings.business_account_id || '',
            verify_token: whatsappSettings.verify_token || '',
            enabled: whatsappSettings.enabled,
            ai_auto_reply_enabled: whatsappSettings.ai_auto_reply_enabled,
            voice_transcription_enabled: whatsappSettings.voice_transcription_enabled,
            response_speed_profile: whatsappSettings.response_speed_profile,
            voice_processing_quick_ack_enabled: whatsappSettings.voice_processing_quick_ack_enabled
          }
        });
      }

      // Meta App ID + Secret → eigener Endpoint (schreibt Server-Env für die
      // Webhook-Signaturprüfung). Nur senden, wenn beides wirklich eingegeben
      // wurde (kein Platzhalter '***SET***').
      if (
        whatsappSettings.meta_app_id &&
        whatsappSettings.meta_app_secret &&
        whatsappSettings.meta_app_secret !== '***SET***'
      ) {
        await axios.post(`${API}/whatsapp/meta-credentials`, {
          app_id: whatsappSettings.meta_app_id,
          app_secret: whatsappSettings.meta_app_secret
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Meta App-Zugangsdaten (Webhook-Signatur) aktualisiert!');
      }
      
      if (failedParts.length === 0) {
        toast.success('Einstellungen gespeichert!');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };
  
  // Save Mobile App Settings separately
  const saveMobileAppSettings = async () => {
    setSavingMobileApp(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/mobile-app/settings`, mobileAppSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mobile App Einstellungen gespeichert!');
    } catch (error) {
      console.error('Error saving mobile app settings:', error);
      toast.error('Fehler beim Speichern der Mobile App Einstellungen');
    } finally {
      setSavingMobileApp(false);
    }
  };
  
  // Save Firebase Settings
  const saveFirebaseSettings = async () => {
    setSavingFirebase(true);
    setFirebaseTestResult(null);
    try {
      const token = localStorage.getItem('token');
      
      // Validate JSON before saving
      let parsedJson;
      try {
        parsedJson = JSON.parse(firebaseSettings.service_account_json);
        if (!parsedJson.project_id || !parsedJson.client_email || !parsedJson.private_key) {
          throw new Error('Ungültige Firebase Service Account JSON');
        }
      } catch (e) {
        toast.error('Ungültige JSON-Datei. Bitte überprüfen Sie das Format.');
        setSavingFirebase(false);
        return;
      }
      
      await axios.put(`${API}/admin/firebase/settings`, {
        service_account_json: firebaseSettings.service_account_json
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state with extracted info
      setFirebaseSettings(prev => ({
        ...prev,
        configured: true,
        project_id: parsedJson.project_id,
        client_email: parsedJson.client_email
      }));
      
      toast.success('Firebase Einstellungen gespeichert!');
    } catch (error) {
      console.error('Error saving firebase settings:', error);
      toast.error('Fehler beim Speichern der Firebase Einstellungen');
    } finally {
      setSavingFirebase(false);
    }
  };
  
  // Test Firebase Connection
  const testFirebaseConnection = async () => {
    setTestingFirebase(true);
    setFirebaseTestResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/firebase/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFirebaseTestResult({ success: true, message: response.data.message });
      toast.success('Firebase Verbindung erfolgreich!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Verbindung fehlgeschlagen';
      setFirebaseTestResult({ success: false, message: errorMsg });
      toast.error(errorMsg);
    } finally {
      setTestingFirebase(false);
    }
  };
  
  // Save Nutrition Bot Settings
  const saveNutritionBotSettings = async () => {
    setSavingNutritionBot(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/portal/ai-assistant/settings`, nutritionBotSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bot-Einstellungen gespeichert!');
    } catch (error) {
      console.error('Error saving nutrition bot settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingNutritionBot(false);
    }
  };
  
  // Knowledge URL functions
  const addKnowledgeUrl = () => {
    if (newKnowledgeUrl && !chatbotSettings.knowledge_urls.includes(newKnowledgeUrl)) {
      setChatbotSettings({
        ...chatbotSettings,
        knowledge_urls: [...chatbotSettings.knowledge_urls, newKnowledgeUrl]
      });
      setNewKnowledgeUrl('');
    }
  };
  
  const removeKnowledgeUrl = (url) => {
    setChatbotSettings({
      ...chatbotSettings,
      knowledge_urls: chatbotSettings.knowledge_urls.filter(u => u !== url)
    });
  };
  
  const scrapeUrl = async (url) => {
    setScraping(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/chatbot/scrape-url`, { url }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('URL erfolgreich gescraped!');
      loadAllSettings();
    } catch (error) {
      toast.error('Fehler beim Scrapen der URL');
    } finally {
      setScraping(false);
    }
  };
  
  // Document upload
  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    if (documentTopic.trim()) {
      formData.append('topic', documentTopic.trim());
    }
    
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API}/chatbot/documents`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      if (data.extracted_preview) {
        const isPdf = /\.pdf$/i.test(file.name || '');
        toast.success(isPdf ? '📄 PDF gelesen — Wissen übernommen!' : '📸 Foto von der KI gelesen — Wissen übernommen!', { duration: 5000 });
        setPhotoExtractPreview(data.extracted_preview);
      } else {
        toast.success('Dokument hochgeladen!');
      }
      setDocumentTopic('');
      loadAllSettings();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploading(false);
    }
  };

  // 08/2026: Manuelles Wissen (Frage+Antwort oder Freitext) eintragen
  const saveManualKnowledge = async () => {
    if (!manualKnowledgeContent.trim()) {
      toast.error('Bitte Inhalt eingeben');
      return;
    }
    setSavingManualKnowledge(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/chatbot/documents/manual`, {
        title: manualKnowledgeTitle,
        content: manualKnowledgeContent,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Wissen gespeichert — der Bot nutzt es sofort!');
      setManualKnowledgeTitle('');
      setManualKnowledgeContent('');
      loadAllSettings();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingManualKnowledge(false);
    }
  };
  
  const deleteDocument = async (docId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/chatbot/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Dokument gelöscht');
      loadAllSettings();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Export Knowledge Base
  const handleExportKnowledge = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/chatbot/knowledge/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const exportData = response.data;
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `wissensdatenbank_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`Export erfolgreich: ${exportData.total_documents} Dokumente`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export fehlgeschlagen');
    }
  };

  // Import Knowledge Base
  const handleImportKnowledge = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const text = await file.text();
      const importData = JSON.parse(text);
      
      // Validate structure
      if (!importData.documents && !importData.knowledge_content) {
        throw new Error('Ungültiges Dateiformat');
      }
      
      const confirmMsg = `Import von ${importData.exported_from || 'unbekannt'}\n\n` +
        `📄 ${importData.documents?.length || 0} Dokumente\n` +
        `🔗 ${importData.knowledge_urls?.length || 0} URLs\n` +
        `📝 ${importData.content_length || 0} Zeichen Inhalt\n\n` +
        `Bestehende Dokumente werden aktualisiert, neue werden hinzugefügt.\n\nFortfahren?`;
      
      if (!window.confirm(confirmMsg)) {
        e.target.value = '';
        return;
      }
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/chatbot/knowledge/import`, {
        documents: importData.documents || [],
        knowledge_urls: importData.knowledge_urls || [],
        knowledge_content: importData.knowledge_content || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      loadAllSettings();
      
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Import fehlgeschlagen: ' + (error.response?.data?.detail || error.message));
    } finally {
      e.target.value = '';
    }
  };
  
  // Test chatbot
  const testChatbot = async () => {
    if (!testMessage) return;
    setTestingChatbot(true);
    setTestResponse('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/chatbot/test`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { message: testMessage }
      });
      setTestResponse(response.data.response || 'Keine Antwort erhalten');
    } catch (error) {
      console.error('Chatbot test error:', error);
      toast.error('Chatbot-Test fehlgeschlagen');
      setTestResponse('Fehler: ' + (error.response?.data?.message || error.message));
    } finally {
      setTestingChatbot(false);
    }
  };

  // ============================================================================
  // EXPORT/IMPORT ALL KI & API SETTINGS
  // ============================================================================
  
  const exportAllSettings = () => {
    try {
      const exportData = {
        export_version: "1.0",
        exported_at: new Date().toISOString(),
        exported_from: window.location.hostname,
        
        // AI Provider Settings (ohne API Keys)
        ai_settings: {
          use_emergent_key: aiSettings.use_emergent_key,
          primary_provider: aiSettings.primary_provider,
          fallback_enabled: aiSettings.fallback_enabled,
          fallback_provider: aiSettings.fallback_provider,
          use_emergent_fallback: aiSettings.use_emergent_fallback,
          nutrition_ai_enabled: aiSettings.nutrition_ai_enabled,
          nutrition_quick_create_enabled: aiSettings.nutrition_quick_create_enabled,
          nutrition_photo_analysis: aiSettings.nutrition_photo_analysis,
          mobile_ai_enabled: aiSettings.mobile_ai_enabled,
          product_scoring_enabled: aiSettings.product_scoring_enabled,
          product_scoring_nutrition_enabled: aiSettings.product_scoring_nutrition_enabled,
          product_scoring_additives_enabled: aiSettings.product_scoring_additives_enabled,
          product_scoring_bio_enabled: aiSettings.product_scoring_bio_enabled,
          product_scoring_weight_nutrition: aiSettings.product_scoring_weight_nutrition,
          product_scoring_weight_additives: aiSettings.product_scoring_weight_additives,
          product_scoring_weight_bio: aiSettings.product_scoring_weight_bio,
          product_scoring_bio_unknown_policy: aiSettings.product_scoring_bio_unknown_policy,
          product_scoring_show_search: aiSettings.product_scoring_show_search,
          product_scoring_show_barcode: aiSettings.product_scoring_show_barcode,
          product_scoring_show_product_detail: aiSettings.product_scoring_show_product_detail,
          product_scoring_show_favorites_recent: aiSettings.product_scoring_show_favorites_recent,
        },
        
        // Complete Chatbot Settings
        chatbot_settings: {
          enabled: chatbotSettings.enabled,
          name: chatbotSettings.name,
          avatar_url: chatbotSettings.avatar_url,
          provider: chatbotSettings.provider,
          model: chatbotSettings.model,
          use_emergent_key: chatbotSettings.use_emergent_key,
          role_description: chatbotSettings.role_description,
          style_description: chatbotSettings.style_description,
          boundaries: chatbotSettings.boundaries,
          booking_enabled: chatbotSettings.booking_enabled,
          booking_advance_hours: chatbotSettings.booking_advance_hours,
          booking_categories_new_customer: chatbotSettings.booking_categories_new_customer,
          booking_categories_existing_customer: chatbotSettings.booking_categories_existing_customer,
          knowledge_urls: chatbotSettings.knowledge_urls,
          knowledge_content: chatbotSettings.knowledge_content,
        },
        
        // Mobile App Settings
        mobile_app_settings: mobileAppSettings,
      };
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ki_api_einstellungen_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('KI & API Einstellungen exportiert! (API-Keys sind NICHT enthalten)');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export fehlgeschlagen');
    }
  };
  
  const importAllSettings = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const text = await file.text();
      const importData = JSON.parse(text);
      
      // Validate structure
      if (!importData.export_version || !importData.chatbot_settings) {
        throw new Error('Ungültiges Dateiformat. Bitte eine gültige Export-Datei verwenden.');
      }
      
      const confirmMsg = `KI & API Einstellungen importieren?\n\n` +
        `📅 Exportiert am: ${new Date(importData.exported_at).toLocaleString('de-DE')}\n` +
        `🌐 Von: ${importData.exported_from || 'unbekannt'}\n\n` +
        `Enthält:\n` +
        `• KI Provider Einstellungen\n` +
        `• Chatbot Konfiguration (Name, Rolle, Stil, Grenzen)\n` +
        `• Buchungseinstellungen\n` +
        `• Wissensdatenbank URLs\n` +
        `• Mobile App Einstellungen\n\n` +
        `⚠️ API-Keys werden NICHT importiert!\n\n` +
        `Bestehende Einstellungen werden überschrieben. Fortfahren?`;
      
      if (!window.confirm(confirmMsg)) {
        e.target.value = '';
        return;
      }
      
      // Apply AI Settings
      if (importData.ai_settings) {
        setAiSettings(prev => ({
          ...prev,
          ...importData.ai_settings,
          // Keep existing API keys
          emergent_key: prev.emergent_key,
          openai_key: prev.openai_key,
          anthropic_key: prev.anthropic_key,
          google_key: prev.google_key,
          serpapi_key: prev.serpapi_key,
        }));
      }
      
      // Apply Chatbot Settings
      if (importData.chatbot_settings) {
        setChatbotSettings(prev => ({
          ...prev,
          ...importData.chatbot_settings,
          // Keep existing API key
          custom_api_key: prev.custom_api_key,
        }));
      }
      
      // Apply Mobile App Settings
      if (importData.mobile_app_settings) {
        setMobileAppSettings(prev => ({
          ...prev,
          ...importData.mobile_app_settings,
        }));
      }
      
      toast.success('Einstellungen importiert! Bitte speichern Sie die Änderungen.');
      
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Import fehlgeschlagen: ' + error.message);
    } finally {
      e.target.value = '';
    }
  };

  const copyWebhookUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('URL kopiert!');
  };

  const getCurrentProviderName = () => {
    if (aiSettings.use_emergent_key) return 'Emergent Universal Key';
    const provider = AI_PROVIDERS.find(p => p.id === aiSettings.primary_provider);
    return provider?.name || 'Nicht konfiguriert';
  };
  
  // Test API Key function
  const [testingApi, setTestingApi] = useState(false);
  const [apiTestResult, setApiTestResult] = useState(null);
  const [testingProvider, setTestingProvider] = useState('');
  const [providerTestStatuses, setProviderTestStatuses] = useState({
    emergent: null,
    openai: null,
    anthropic: null,
    google: null,
    serpapi: null,
  });

  const getProviderTestName = (providerId) => {
    if (providerId === 'emergent') return 'Emergent';
    if (providerId === 'serpapi') return 'SerpAPI';
    return AI_PROVIDERS.find(p => p.id === providerId)?.name || providerId;
  };

  const normalizeKeyForTest = (key) => {
    if (!key) return null;
    if (String(key).includes('...')) return null;
    return key;
  };

  const testSingleProvider = async (providerId, options = {}) => {
    const { silent = false, skipSave = false } = options;
    const token = localStorage.getItem('token');

    let apiKey = null;
    if (providerId === 'emergent') {
      apiKey = normalizeKeyForTest(aiSettings.emergent_key);
    } else {
      apiKey = normalizeKeyForTest(aiSettings[`${providerId}_key`]);
    }

    setTestingProvider(providerId);
    try {
      const response = await axios.post(
        `${API}/settings/ai/test`,
        {
          provider: providerId,
          ...(apiKey ? { api_key: apiKey } : {})
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const success = !!response.data?.success;
      const message = response.data?.message || response.data?.error || 'Unbekanntes Ergebnis';
      const newStatus = {
        success,
        message,
        tested_at: new Date().toISOString()
      };

      setProviderTestStatuses(prev => {
        const updated = {
          ...prev,
          [providerId]: newStatus
        };
        
        // Save to database if not skipping
        if (!skipSave) {
          axios.put(`${API}/settings/ai/test-statuses`, updated, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(err => console.error('Error saving test status:', err));
        }
        
        return updated;
      });

      if (!silent) {
        if (success) {
          toast.success(`${getProviderTestName(providerId)} getestet: OK`);
        } else {
          toast.error(`${getProviderTestName(providerId)} Test fehlgeschlagen`);
        }
      }

      return { success, message };
    } catch (error) {
      const message = error.response?.data?.detail || error.response?.data?.error || error.message;
      const newStatus = {
        success: false,
        message,
        tested_at: new Date().toISOString()
      };

      setProviderTestStatuses(prev => {
        const updated = {
          ...prev,
          [providerId]: newStatus
        };
        
        // Save to database if not skipping
        if (!skipSave) {
          axios.put(`${API}/settings/ai/test-statuses`, updated, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(err => console.error('Error saving test status:', err));
        }
        
        return updated;
      });

      if (!silent) {
        toast.error(`${getProviderTestName(providerId)} Test fehlgeschlagen`);
      }

      return { success: false, message };
    } finally {
      setTestingProvider('');
    }
  };
  
  const testApiKey = async () => {
    setTestingApi(true);
    setApiTestResult(null);
    try {
      const providersToTest = ['emergent', 'openai', 'anthropic', 'google'];
      const results = [];
      const newStatuses = {};

      for (const providerId of providersToTest) {
        const result = await testSingleProvider(providerId, { silent: true, skipSave: true });
        results.push({ providerId, ...result });
        newStatuses[providerId] = {
          success: result.success,
          message: result.message,
          tested_at: new Date().toISOString()
        };
      }

      // Save all test statuses to database at once
      const token = localStorage.getItem('token');
      try {
        await axios.put(`${API}/settings/ai/test-statuses`, newStatuses, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (saveError) {
        console.error('Error saving test statuses:', saveError);
      }

      const successCount = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).map(r => getProviderTestName(r.providerId));

      if (successCount === results.length) {
        setApiTestResult({
          success: true,
          message: `Alle API-Provider erfolgreich getestet (${successCount}/${results.length}) ✅`
        });
        toast.success('Alle API-Tests erfolgreich');
      } else {
        setApiTestResult({
          success: false,
          message: `Ergebnis: ${successCount}/${results.length} erfolgreich. Fehler bei: ${failed.join(', ')}`
        });
        toast.error('Nicht alle API-Tests waren erfolgreich');
      }
    } catch (error) {
      console.error('API test error:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message;
      setApiTestResult({ success: false, message: `Fehler: ${errorMsg}` });
      toast.error('API-Key funktioniert nicht');
    } finally {
      setTestingApi(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-purple-600" />
        <span className="ml-2 text-gray-600">Lade Einstellungen...</span>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Sparkles className="text-purple-600" />
            {group ? GROUP_META[group]?.title : 'KI & API Einstellungen'}
          </h2>
          <p className="text-gray-600 mt-1">{group ? GROUP_META[group]?.description : 'Alle Schnittstellen zentral verwalten'}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Export Button */}
          <Button 
            variant="outline" 
            onClick={exportAllSettings}
            className="border-green-300 text-green-700 hover:bg-green-50"
            data-testid="export-settings-btn"
          >
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
          
          {/* Import Button */}
          <input
            type="file"
            ref={configImportRef}
            accept=".json"
            onChange={importAllSettings}
            className="hidden"
          />
          <Button 
            variant="outline"
            onClick={() => configImportRef.current?.click()}
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid="import-settings-btn"
          >
            <UploadIcon className="mr-2 h-4 w-4" />
            Import
          </Button>
          
          {/* Save Button */}
          <Button onClick={saveSettings} disabled={saving} data-testid="save-all-btn">
            {saving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Speichern
          </Button>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="flex flex-wrap gap-1 h-auto p-1">
          {isTabVisible('ki-allgemein') && (
          <TabsTrigger value="ki-allgemein" className="flex items-center gap-1" data-testid="tab-ki-provider">
            <Key className="w-4 h-4" />
            <span className="hidden sm:inline">KI-Provider</span>
          </TabsTrigger>
          )}
          {isTabVisible('ki-modelle') && (
          <TabsTrigger value="ki-modelle" className="flex items-center gap-1" data-testid="tab-ki-modelle">
            <Sparkles className="w-4 h-4" />
            <span className="hidden sm:inline">KI-Modelle</span>
          </TabsTrigger>
          )}
          {isTabVisible('ki-automatik') && (
          <TabsTrigger value="ki-automatik" className="flex items-center gap-1" data-testid="tab-ki-automatik">
            <Sparkles className="w-4 h-4" />
            <span className="hidden sm:inline">KI & Automatik</span>
          </TabsTrigger>
          )}
          {isTabVisible('marketing') && (
          <TabsTrigger value="marketing" className="flex items-center gap-1" data-testid="tab-marketing">
            <Heart className="w-4 h-4" />
            <span className="hidden sm:inline">Marketing</span>
          </TabsTrigger>
          )}
          {isTabVisible('kommunikation') && (
          <TabsTrigger value="kommunikation" className="flex items-center gap-1" data-testid="tab-kommunikation">
            <Bot className="w-4 h-4" />
            <span className="hidden sm:inline">Kommunikation</span>
          </TabsTrigger>
          )}
          {isTabVisible('ki-ernährung') && (
          <TabsTrigger value="ki-ernährung" className="flex items-center gap-1" data-testid="tab-ernährung">
            <Apple className="w-4 h-4" />
            <span className="hidden sm:inline">Portal Ernährungs-KI</span>
          </TabsTrigger>
          )}
          {isTabVisible('firebase') && (
          <TabsTrigger value="firebase" className="flex items-center gap-1" data-testid="tab-firebase">
            <Bell className="w-4 h-4" />
            <span className="hidden sm:inline">Firebase</span>
          </TabsTrigger>
          )}
          {isTabVisible('email-smtp') && (
          <TabsTrigger value="email-smtp" className="flex items-center gap-1" data-testid="tab-email">
            <Mail className="w-4 h-4" />
            <span className="hidden sm:inline">Email SMTP</span>
          </TabsTrigger>
          )}
          {isTabVisible('whatsapp-api') && (
          <TabsTrigger value="whatsapp-api" className="flex items-center gap-1" data-testid="tab-wa-api">
            <MessageCircle className="w-4 h-4" />
            <span className="hidden sm:inline">WhatsApp API</span>
          </TabsTrigger>
          )}
          {isTabVisible('zahlungen') && (
          <TabsTrigger value="zahlungen" className="flex items-center gap-1" data-testid="tab-zahlungen">
            <CreditCard className="w-4 h-4" />
            <span className="hidden sm:inline">Zahlungen</span>
          </TabsTrigger>
          )}
        </TabsList>
        
        {/* ============ KI Provider Tab ============ */}
        <TabsContent value="ki-allgemein" className="space-y-4">
          <Card className="border-2 border-purple-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="text-purple-600" />
                API-Schlüssel Auswahl
              </CardTitle>
              <CardDescription>
                Wählen Sie zwischen dem Emergent Universal Key oder eigenen API-Schlüsseln
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <RadioGroup
                value={aiSettings.use_emergent_key ? 'emergent' : 'custom'}
                onValueChange={(val) => setAiSettings({ 
                  ...aiSettings, 
                  use_emergent_key: val === 'emergent',
                  primary_provider: val === 'emergent' ? 'emergent' : (aiSettings.primary_provider === 'emergent' ? 'openai' : aiSettings.primary_provider)
                })}
                className="space-y-3"
              >
                <div className={`flex items-start space-x-3 p-4 rounded-lg border-2 transition-all cursor-pointer ${
                  aiSettings.use_emergent_key ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                }`}
                onClick={() => setAiSettings({ ...aiSettings, use_emergent_key: true, primary_provider: 'emergent' })}
                >
                  <RadioGroupItem value="emergent" id="key-emergent" className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-purple-600" />
                      <Label htmlFor="key-emergent" className="font-semibold text-lg cursor-pointer">
                        Emergent Universal Key
                      </Label>
                      <Badge className="bg-purple-600">Empfohlen</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Ein Key für OpenAI, Anthropic & Google. Keine separate Anmeldung erforderlich.
                    </p>
                    {aiSettings.use_emergent_key && (
                      <div className="mt-4 space-y-3">
                        <Input
                          type="password"
                          placeholder="ek_..."
                          value={aiSettings.emergent_key || ''}
                          onChange={(e) => setAiSettings({ ...aiSettings, emergent_key: e.target.value })}
                          data-testid="emergent-key-input"
                        />
                        <div className="flex items-center gap-2 flex-wrap" data-testid="emergent-api-test-controls">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => testSingleProvider('emergent')}
                            disabled={testingProvider === 'emergent'}
                            className="border-purple-500 text-purple-700 hover:bg-purple-100"
                            data-testid="provider-api-test-button-emergent"
                          >
                            {testingProvider === 'emergent' ? (
                              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Prüfe…</>
                            ) : (
                              <><Check className="w-4 h-4 mr-2" /> Bestätigen</>
                            )}
                          </Button>
                          {providerTestStatuses.emergent && (
                            <span
                              className={`text-xs px-2 py-1 rounded-full ${providerTestStatuses.emergent.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}
                              data-testid="provider-api-test-status-emergent"
                            >
                              {providerTestStatuses.emergent.success ? 'Bestätigt' : 'Fehler'}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-purple-700 flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" />
                          Erhältlich unter: Profile → Universal Key
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className={`flex items-start space-x-3 p-4 rounded-lg border-2 transition-all cursor-pointer ${
                  !aiSettings.use_emergent_key ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                }`}
                onClick={() => setAiSettings({ ...aiSettings, use_emergent_key: false, primary_provider: aiSettings.primary_provider === 'emergent' ? 'openai' : aiSettings.primary_provider })}
                >
                  <RadioGroupItem value="custom" id="key-custom" className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Key className="w-5 h-5 text-blue-600" />
                      <Label htmlFor="key-custom" className="font-semibold text-lg cursor-pointer">
                        Eigene API-Schlüssel
                      </Label>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Verwenden Sie Ihre eigenen API-Keys von OpenAI, Anthropic oder Google.
                    </p>
                    {!aiSettings.use_emergent_key && (
                      <div className="mt-4 space-y-4">
                        <div className="space-y-2">
                          <Label>Primärer KI-Provider</Label>
                          <select
                            className="w-full p-2 border rounded-md"
                            value={aiSettings.primary_provider}
                            onChange={(e) => setAiSettings({ ...aiSettings, primary_provider: e.target.value })}
                          >
                            {AI_PROVIDERS.map(p => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                        </div>
                        {AI_PROVIDERS.map(provider => (
                          <div key={provider.id} className={`p-3 border rounded-lg ${
                            aiSettings.primary_provider === provider.id ? 'border-blue-300 bg-blue-50/50' : ''
                          }`}>
                            <h4 className="font-medium mb-2">{provider.name} API Key</h4>
                            <div className="space-y-2">
                              <Input
                                type="password"
                                placeholder={provider.placeholder}
                                value={aiSettings[`${provider.id}_key`] || ''}
                                onChange={(e) => setAiSettings({ ...aiSettings, [`${provider.id}_key`]: e.target.value })}
                                data-testid={`provider-key-input-${provider.id}`}
                              />
                              <div className="flex items-center gap-2 flex-wrap" data-testid={`provider-api-test-controls-${provider.id}`}>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => testSingleProvider(provider.id)}
                                  disabled={testingProvider === provider.id}
                                  className="border-blue-500 text-blue-700 hover:bg-blue-100"
                                  data-testid={`provider-api-test-button-${provider.id}`}
                                >
                                  {testingProvider === provider.id ? (
                                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Prüfe…</>
                                  ) : (
                                    <><Check className="w-4 h-4 mr-2" /> Bestätigen</>
                                  )}
                                </Button>
                                {providerTestStatuses[provider.id] && (
                                  <span
                                    className={`text-xs px-2 py-1 rounded-full ${providerTestStatuses[provider.id].success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}
                                    data-testid={`provider-api-test-status-${provider.id}`}
                                  >
                                    {providerTestStatuses[provider.id].success ? 'Bestätigt' : 'Fehler'}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </RadioGroup>
              
              {/* API Test Button */}
              <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200" data-testid="all-api-test-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-green-900">Alle API-Verbindungen testen</p>
                    <p className="text-xs text-green-700">Unabhängig vom Chatbot-Status (direkter API-Check pro Provider)</p>
                  </div>
                  <Button 
                    onClick={testApiKey} 
                    disabled={testingApi}
                    variant="outline"
                    className="border-green-500 text-green-700 hover:bg-green-100"
                    data-testid="test-all-apis-button"
                  >
                    {testingApi ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Teste...</>
                    ) : (
                      <><Check className="w-4 h-4 mr-2" /> Alle APIs testen</>
                    )}
                  </Button>
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="provider-api-test-summary-grid">
                  {['emergent', 'openai', 'anthropic', 'google'].map((providerId) => {
                    const status = providerTestStatuses[providerId];
                    return (
                      <div
                        key={providerId}
                        className="p-2 rounded border bg-white/70 text-xs flex items-center justify-between gap-2"
                        data-testid={`provider-api-test-summary-${providerId}`}
                      >
                        <span className="font-medium text-gray-700">{getProviderTestName(providerId)}</span>
                        <span className={`px-2 py-0.5 rounded-full ${
                          !status ? 'bg-gray-100 text-gray-600' : status.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'
                        }`}>
                          {!status ? 'Nicht getestet' : status.success ? 'Bestätigt' : 'Fehler'}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {apiTestResult && (
                  <div className={`mt-3 p-3 rounded ${apiTestResult.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-testid="all-api-test-result-message">
                    {apiTestResult.message}
                  </div>
                )}
              </div>

              <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200" data-testid="serpapi-settings-card">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-amber-900">Web-Bildsuche für fehlende Produktfotos (SerpAPI)</p>
                    <p className="text-xs text-amber-700">
                      Wird vom Admin-Bild-Recovery genutzt: zuerst Barcode/OFF, danach Websuche (streng).
                    </p>
                  </div>
                  <Badge variant="outline" className="text-amber-800 border-amber-400" data-testid="serpapi-strict-mode-badge">
                    Strict Mode
                  </Badge>
                </div>
                <div className="mt-3 space-y-2">
                  <Label htmlFor="serpapi-key-input">SerpAPI Key</Label>
                  <Input
                    id="serpapi-key-input"
                    type="password"
                    placeholder="3f4..."
                    value={aiSettings.serpapi_key || ''}
                    onChange={(e) => setAiSettings({ ...aiSettings, serpapi_key: e.target.value })}
                    data-testid="serpapi-key-input"
                  />
                  <div className="flex items-center gap-2 flex-wrap" data-testid="serpapi-api-test-controls">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => testSingleProvider('serpapi')}
                      disabled={testingProvider === 'serpapi'}
                      className="border-amber-500 text-amber-800 hover:bg-amber-100"
                      data-testid="provider-api-test-button-serpapi"
                    >
                      {testingProvider === 'serpapi' ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Prüfe…</>
                      ) : (
                        <><Check className="w-4 h-4 mr-2" /> Bestätigen</>
                      )}
                    </Button>
                    {providerTestStatuses.serpapi && (
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${providerTestStatuses.serpapi.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}
                        data-testid="provider-api-test-status-serpapi"
                      >
                        {providerTestStatuses.serpapi.success ? 'Bestätigt' : 'Fehler'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-amber-700" data-testid="serpapi-help-text">
                    Key erstellen unter serpapi.com/dashboard
                  </p>
                </div>
              </div>

              {/* Refdata API Key (Schweizer Ärzte-/GLN-Suche, Physio-Modul) */}
              <div className="p-4 bg-sky-50 rounded-lg border-2 border-sky-200" data-testid="refdata-settings-card">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-sky-900">Refdata – Ärzte-/GLN-Suche (Physio-Verordnungen)</p>
                    <p className="text-xs text-sky-700">
                      Schweizer Referenzdaten für die automatische Arztsuche per GLN. Ohne Key bleibt die manuelle Arzt-Erfassung aktiv.
                    </p>
                  </div>
                  <Badge variant="outline" className="text-sky-800 border-sky-400" data-testid="refdata-badge">
                    Optional
                  </Badge>
                </div>
                <div className="mt-3 space-y-2">
                  <Label htmlFor="refdata-key-input">Refdata Subscription-Key</Label>
                  <Input
                    id="refdata-key-input"
                    type="password"
                    placeholder="z.B. 0a1b2c3d..."
                    value={aiSettings.refdata_api_key || ''}
                    onChange={(e) => setAiSettings({ ...aiSettings, refdata_api_key: e.target.value })}
                    data-testid="refdata-key-input"
                  />
                  <p className="text-xs text-sky-700" data-testid="refdata-help-text">
                    Key beantragen unter developer.refdata.ch (Produkt „Partner Public Online"). Nach dem Speichern wird die echte GLN-Suche aktiv.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Fallback Provider */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="text-orange-600" />
                Fallback KI-Provider
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div>
                  <p className="font-medium text-orange-900">Fallback aktivieren</p>
                  <p className="text-xs text-orange-700">Automatisch zu Backup wechseln bei Ausfall</p>
                </div>
                <Switch
                  checked={aiSettings.fallback_enabled}
                  onCheckedChange={(checked) => setAiSettings({ ...aiSettings, fallback_enabled: checked })}
                />
              </div>
              {aiSettings.fallback_enabled && (
                <div className="p-4 bg-gray-50 rounded-lg border">
                  <p className="text-sm text-gray-600">
                    Aktive Konfiguration: <span className="font-medium">{getCurrentProviderName()}</span>
                    <ArrowRight className="w-4 h-4 inline mx-2" />
                    <span className="text-orange-600 font-medium">
                      {aiSettings.use_emergent_fallback ? 'Emergent' : AI_PROVIDERS.find(p => p.id === aiSettings.fallback_provider)?.name}
                    </span>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* ============ KI-Modelle pro Dienst Tab (NEW) ============ */}
        <TabsContent value="ki-modelle" className="space-y-4">
          <AIOptimizerCard />
          <Card className="border-2 border-indigo-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="text-indigo-600" />
                KI-Modelle pro Dienst
              </CardTitle>
              <CardDescription>
                Wähle für jeden KI-Dienst das passende Modell. Jeder Dienst hat eine Empfehlung basierend auf Qualität, Geschwindigkeit und Kosten.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Provider Legend */}
              <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg">
                <span className="text-xs font-medium text-gray-600">Anbieter:</span>
                <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">OpenAI</span>
                <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">Anthropic</span>
                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Google</span>
              </div>

              <Card className="border-emerald-200" data-testid="ai-model-application-blocks-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">KI-Anwendungsblöcke</CardTitle>
                  <CardDescription>
                    Hier siehst du alle KI-Anwendungen und welches Modell aktuell zugeordnet ist.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {AI_APPLICATION_BLOCKS.map((block) => (
                    <div key={block.id} className="space-y-2" data-testid={`ai-application-block-${block.id}`}>
                      <p className="text-sm font-semibold text-zinc-800">{block.title}</p>
                      <div className="grid gap-2">
                        {block.apps.map((app) => {
                          if (!app.serviceId) {
                            return (
                              <div
                                key={`${block.id}-${app.label}`}
                                className="flex items-center justify-between rounded-lg border bg-zinc-50 px-3 py-2"
                                data-testid={`ai-application-item-${block.id}-${app.label.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
                              >
                                <div>
                                  <p className="text-sm text-zinc-900">{app.label}</p>
                                  <p className="text-xs text-zinc-500">{app.note}</p>
                                </div>
                                <Badge variant="secondary" data-testid={`ai-application-item-note-${block.id}`}>Kein Modell</Badge>
                              </div>
                            );
                          }

                          const service = getServiceMeta(app.serviceId);
                          const config = getServiceConfig(app.serviceId);
                          const isConfigured = Boolean(service);

                          return (
                            <div
                              key={`${block.id}-${app.serviceId}`}
                              className="flex items-center justify-between rounded-lg border bg-white px-3 py-2 gap-3"
                              data-testid={`ai-application-item-${app.serviceId}`}
                            >
                              <div>
                                <p className="text-sm text-zinc-900">{app.label}</p>
                                <p className="text-xs text-zinc-500">
                                  {isConfigured
                                    ? `${config.provider} / ${config.model}`
                                    : 'Noch kein KI-Modell-Block vorhanden'}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge
                                  variant={isConfigured ? 'outline' : 'destructive'}
                                  data-testid={`ai-application-item-status-${app.serviceId}`}
                                >
                                  {isConfigured ? 'Verknüpft' : 'Fehlt'}
                                </Badge>
                                {isConfigured && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => scrollToServiceCard(app.serviceId)}
                                    data-testid={`ai-application-item-jump-${app.serviceId}`}
                                  >
                                    Zum Modell
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
              
              {/* Service Model Cards */}
              <div className="grid gap-4">
                {aiServices.map((service) => {
                  const currentConfig = serviceModels[service.id] || {
                    provider: service.recommended_provider,
                    model: service.recommended_model,
                    enabled: true
                  };
                  const isRecommended = currentConfig.model === service.recommended_model;
                  
                  return (
                    <div 
                      key={service.id}
                      id={`ai-service-card-${service.id}`}
                      data-testid={`ai-service-card-${service.id}`}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        currentConfig.enabled 
                          ? 'border-indigo-200 bg-white' 
                          : 'border-gray-200 bg-gray-50 opacity-60'
                      }`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        {/* Service Info */}
                        <div className="flex items-start gap-3">
                          <div className="text-2xl w-8 text-center shrink-0">{/^\p{Emoji}/u.test(service.name) ? service.name.match(/^\p{Emoji_Presentation}|\p{Emoji}\uFE0F?/u)?.[0] || '⚙' : '⚙'}</div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold text-sm">{service.name.replace(/^\p{Emoji_Presentation}\s*|\p{Emoji}\uFE0F?\s*/u, '').trim() || service.name}</h4>
                              {isRecommended && (
                                <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full whitespace-nowrap">
                                  Empfohlen
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">{service.description}</p>
                            {service.id === 'voice_analysis' && (
                              <div
                                className="mt-2 inline-flex items-center gap-2 text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
                                data-testid="ai-service-voice-nutrition-scope-badge"
                              >
                                <span>Gilt nur für Ernährung (Text→Essen), nicht für WhatsApp.</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Model Selection */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                          <div className="flex items-center gap-2 p-2 rounded-md border bg-zinc-50" data-testid={`ai-service-enabled-wrapper-${service.id}`}>
                            <span className="text-xs text-zinc-600">Aktiv</span>
                            <Switch
                              checked={currentConfig.enabled !== false}
                              onCheckedChange={(checked) => {
                                const newModels = { ...serviceModels };
                                newModels[service.id] = {
                                  ...currentConfig,
                                  enabled: checked
                                };
                                setServiceModels(newModels);
                              }}
                              data-testid={`ai-service-enabled-toggle-${service.id}`}
                            />
                          </div>

                          {/* Provider Select */}
                          <Select
                            value={currentConfig.provider}
                            onValueChange={(value) => {
                              const newModels = { ...serviceModels };
                              newModels[service.id] = {
                                ...currentConfig,
                                provider: value,
                                // Reset to first model of new provider
                                model: availableModels[value]?.[0]?.id || currentConfig.model
                              };
                              setServiceModels(newModels);
                            }}
                          >
                            <SelectTrigger className="w-[130px]" data-testid={`ai-service-provider-select-${service.id}`}>
                              <SelectValue placeholder="Anbieter" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="openai">OpenAI</SelectItem>
                              <SelectItem value="anthropic">Anthropic</SelectItem>
                              <SelectItem value="google">Google</SelectItem>
                            </SelectContent>
                          </Select>
                          
                          {/* Model Select */}
                          <Select
                            value={currentConfig.model}
                            onValueChange={(value) => {
                              const newModels = { ...serviceModels };
                              newModels[service.id] = {
                                ...currentConfig,
                                model: value
                              };
                              setServiceModels(newModels);
                            }}
                          >
                            <SelectTrigger className="w-[160px]" data-testid={`ai-service-model-select-${service.id}`}>
                              <SelectValue placeholder="Modell" />
                            </SelectTrigger>
                            <SelectContent>
                              {(availableModels[currentConfig.provider] || []).map((model) => (
                                <SelectItem key={model.id} value={model.id}>
                                  {model.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      {/* Recommendation Info */}
                      {!isRecommended && (
                        <div className="mt-3 p-2 bg-amber-50 rounded text-xs text-amber-800 flex items-start gap-2">
                          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <div>
                            <strong>Empfehlung:</strong> {service.recommended_model} ({service.recommended_provider})
                            <br />
                            <span className="text-amber-600">{service.reason}</span>
                          </div>
                        </div>
                      )}
                      
                      {isRecommended && (
                        <div className="mt-3 p-2 bg-green-50 rounded text-xs text-green-700">
                          💡 {service.reason}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Save Button */}
              <div className="flex justify-end pt-4 border-t">
                <Button
                  onClick={saveServiceModels}
                  disabled={savingServiceModels}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {savingServiceModels ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Speichern...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      KI-Modelle speichern
                    </>
                  )}
                </Button>
              </div>
              
              {/* Reset to Recommended */}
              <div className="flex justify-center pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const recommended = {};
                    aiServices.forEach(s => {
                      recommended[s.id] = {
                        provider: s.recommended_provider,
                        model: s.recommended_model,
                        enabled: true
                      };
                    });
                    setServiceModels(recommended);
                    toast.info('Alle Modelle auf Empfehlung zurückgesetzt');
                  }}
                  className="text-indigo-600 hover:text-indigo-800"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Alle auf Empfehlung zurücksetzen
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Audio & Transkription */}
          <Card className="border-2 border-blue-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mic className="text-blue-600" />
                Audio & Transkription
              </CardTitle>
              <CardDescription>
                Einstellungen für Sprachnachrichten-Transkription (WhatsApp).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Voice Transcription Toggle */}
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div>
                  <p className="font-medium text-blue-900">Sprachnachrichten-Transkription (WhatsApp)</p>
                  <p className="text-xs text-blue-700">
                    Audio wird mit <strong>whisper-1</strong> in Text umgewandelt. Wenn deaktiviert, wird nur [Sprachnachricht] gespeichert.
                  </p>
                </div>
                <Switch
                  checked={whatsappSettings.voice_transcription_enabled}
                  onCheckedChange={(checked) => setWhatsappSettings({ ...whatsappSettings, voice_transcription_enabled: checked })}
                  data-testid="whatsapp-voice-transcription-switch"
                />
              </div>

              {/* Quick Acknowledgment */}
              <div className="flex items-center justify-between p-4 bg-blue-50/50 rounded-lg border border-blue-200">
                <div>
                  <p className="font-medium text-blue-900">Sofort-Bestätigung bei Audio</p>
                  <p className="text-xs text-blue-700">
                    Sendet sofort: „Sprachnachricht erhalten…", während Transkription verarbeitet wird.
                  </p>
                </div>
                <Switch
                  checked={whatsappSettings.voice_processing_quick_ack_enabled}
                  onCheckedChange={(checked) => setWhatsappSettings({ ...whatsappSettings, voice_processing_quick_ack_enabled: checked })}
                  data-testid="whatsapp-voice-quick-ack-switch"
                />
              </div>
            </CardContent>
          </Card>
          
          {/* Info Card */}
          <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-indigo-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-indigo-800 mb-2">Hinweise zu den Modellen:</p>
                  <ul className="space-y-1 text-indigo-700">
                    <li>• <strong>GPT-5.2</strong> - Neuestes OpenAI Modell, beste Qualität für komplexe Aufgaben</li>
                    <li>• <strong>GPT-4o</strong> - Multimodal (Vision), ideal für Bildanalyse</li>
                    <li>• <strong>GPT-4o-mini</strong> - Schnell & günstig, gut für einfache Aufgaben</li>
                    <li>• <strong>Claude Sonnet</strong> - Anthropic's stärkstes Modell, sehr gute Qualität</li>
                    <li>• <strong>Gemini 2.5</strong> - Google's neuestes Modell, schnell und vielseitig</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* ============ KI & Automatik ============ */}
        <TabsContent value="ki-automatik" className="space-y-4" data-testid="tab-content-ki-automatik">
          {/* Admin-KI Business-Assistent (inkl. Comeback-Nachricht, Auto-Reports & Team-Alarm-Vorlage) */}
          <AdminAISettingsCard />
          {/* KI-Kündigungsrisiko & Chat-Stimmungsanalyse */}
          <ChurnRiskSettingsCard />
        </TabsContent>

        {/* ============ Marketing ============ */}
        <TabsContent value="marketing" className="space-y-4" data-testid="tab-content-marketing">
          <div className="flex items-center justify-between rounded-lg border border-teal-200 bg-teal-50 px-4 py-3">
            <p className="text-sm text-teal-900">Kampagnen senden, Newsletter & Automationen findest du im Marketing-Bereich.</p>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 gap-2 border-teal-300 text-teal-800 hover:bg-teal-100"
              data-testid="marketing-to-campaigns-btn"
              onClick={() => navigate('/campaigns')}
            >
              Zu den Kampagnen
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Empfehlungsprogramm + proaktiver Empfehlungslink */}
          <ReferralSettingsCard />

          {/* Freundschaftsbaum – Freundeskreis-Preise */}
          <FriendshipTreeSettingsCard />

          {/* Empfehlungs-Übersicht: wer wen geworben + nächste Reduktion */}
          <ReferralOverviewCard />

          {/* Google-Bewertungs-Automatik (global) */}
          <ReviewAutomationCard />
        </TabsContent>

        {/* ============ Kommunikation ============ */}
        <TabsContent value="kommunikation" className="space-y-4" data-testid="tab-content-kommunikation">
          {/* 09/2026: Empfänger für weitergeleitetes Kunden-Feedback */}
          <FeedbackRecipientsPanel />

          {/* Bot Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="text-purple-600" />
                WhatsApp Bot Einstellungen
              </CardTitle>
              <CardDescription>
                Automatischer Kundenservice-Bot für WhatsApp-Nachrichten und Terminbuchungen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div>
                  <p className="font-medium text-purple-900">WhatsApp Bot aktivieren</p>
                  <p className="text-xs text-purple-700">Automatische KI-Antworten auf eingehende WhatsApp-Nachrichten</p>
                </div>
                <Switch
                  checked={chatbotSettings.enabled}
                  onCheckedChange={(checked) => setChatbotSettings({ ...chatbotSettings, enabled: checked })}
                  data-testid="chatbot-enabled-toggle"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div>
                  <p className="font-medium text-amber-900">Emoji-Reaktionen 😊</p>
                  <p className="text-xs text-amber-700">Der Bot reagiert auf jede eingehende Kundennachricht mit einem passenden Emoji</p>
                </div>
                <Switch
                  checked={chatbotSettings.reactions_enabled !== false}
                  onCheckedChange={(checked) => setChatbotSettings({ ...chatbotSettings, reactions_enabled: checked })}
                  data-testid="chatbot-reactions-toggle"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Bot Name</Label>
                  <Input
                    placeholder="z.B. Steven"
                    value={chatbotSettings.name || ''}
                    onChange={(e) => setChatbotSettings({ ...chatbotSettings, name: e.target.value })}
                    data-testid="chatbot-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Avatar URL (optional)</Label>
                  <Input
                    placeholder="https://..."
                    value={chatbotSettings.avatar_url || ''}
                    onChange={(e) => setChatbotSettings({ ...chatbotSettings, avatar_url: e.target.value })}
                  />
                </div>
              </div>
              
              {/* Info: API Key kommt von KI-Provider Tab */}
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  <span className="font-medium text-purple-900">KI-Provider Einstellung</span>
                </div>
                <p className="text-sm text-purple-700">
                  Der Chatbot verwendet automatisch den API-Key aus dem <strong>"KI-Provider"</strong> Tab.
                </p>
                <p className="text-sm text-purple-600 mt-1">
                  Aktuell: <strong>{getCurrentProviderName()}</strong>
                </p>
              </div>
              
              {/* Model - Now configured in KI-Modelle tab */}
              <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-indigo-600" />
                  <span className="text-sm font-medium text-indigo-800">KI-Modell</span>
                </div>
                <p className="text-sm text-indigo-600 mt-1">
                  Das Modell für den Chatbot wird jetzt im <strong>"KI-Modelle"</strong> Tab konfiguriert.
                </p>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 h-auto text-indigo-700 font-medium"
                  onClick={() => setActiveTab('ki-modelle')}
                >
                  → Zum KI-Modelle Tab
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Bot Personality */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="text-blue-600" />
                WhatsApp Bot Persönlichkeit
              </CardTitle>
              <CardDescription>
                Persönlichkeit des WhatsApp-Bots für Kundenservice und Terminbuchungen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <span className="text-lg">📋</span>
                  Rolle & Aufgaben
                </Label>
                <Textarea
                  placeholder="Der Bot ist ein freundlicher Fitness-Studio-Assistent..."
                  value={chatbotSettings.role_description || ''}
                  onChange={(e) => setChatbotSettings({ ...chatbotSettings, role_description: e.target.value })}
                  rows={6}
                  data-testid="chatbot-role"
                />
                <p className="text-xs text-gray-500">Beschreiben Sie detailliert, welche Aufgaben der Bot übernehmen soll.</p>
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <span className="text-lg">💬</span>
                  Stil & Sprache
                </Label>
                <Textarea
                  placeholder="Der Bot kommuniziert freundlich, professionell und vertrauenswürdig..."
                  value={chatbotSettings.style_description || ''}
                  onChange={(e) => setChatbotSettings({ ...chatbotSettings, style_description: e.target.value })}
                  rows={4}
                  data-testid="chatbot-style"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <span className="text-lg">🚫</span>
                  Grenzen & Tabus
                </Label>
                <Textarea
                  placeholder="Der Bot soll keine medizinischen Ratschläge geben..."
                  value={chatbotSettings.boundaries || ''}
                  onChange={(e) => setChatbotSettings({ ...chatbotSettings, boundaries: e.target.value })}
                  rows={4}
                  data-testid="chatbot-boundaries"
                />
              </div>
            </CardContent>
          </Card>

          {/* Booking Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="text-green-600" />
                Terminbuchung via Chat
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                <div>
                  <p className="font-medium text-green-900">Terminbuchung aktivieren</p>
                  <p className="text-xs text-green-700">Kunden können Termine direkt über den Chat buchen</p>
                </div>
                <Switch
                  checked={chatbotSettings.booking_enabled}
                  onCheckedChange={(checked) => setChatbotSettings({ ...chatbotSettings, booking_enabled: checked })}
                  data-testid="chatbot-booking-toggle"
                />
              </div>
              
              {chatbotSettings.booking_enabled && (
                <div className="space-y-2">
                  <Label>Mindest-Vorlaufzeit (Stunden)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={chatbotSettings.booking_advance_hours || 2}
                    onChange={(e) => setChatbotSettings({ ...chatbotSettings, booking_advance_hours: parseInt(e.target.value) })}
                  />
                  <p className="text-xs text-gray-500">Wie viele Stunden vor dem Termin muss mindestens gebucht werden?</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Knowledge Base */}
          <Card className="bg-zinc-900 border-zinc-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Database className="text-orange-500" />
                    Wissensdatenbank
                  </CardTitle>
                  <CardDescription className="text-zinc-400">
                    URLs und Dokumente, die der Bot als Wissensquelle nutzt
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleExportKnowledge}
                    className="text-teal-400 border-teal-600 hover:bg-teal-900/50 bg-zinc-800"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Exportieren
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => knowledgeImportRef.current?.click()}
                    className="text-orange-400 border-orange-600 hover:bg-orange-900/50 bg-zinc-800"
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Importieren
                  </Button>
                  <input
                    type="file"
                    ref={knowledgeImportRef}
                    onChange={handleImportKnowledge}
                    accept=".json"
                    className="hidden"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* URL Input */}
              <div className="space-y-2">
                <Label className="text-zinc-300">Website URL hinzufügen</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="https://www.example.com"
                    value={newKnowledgeUrl}
                    onChange={(e) => setNewKnowledgeUrl(e.target.value)}
                    className="bg-zinc-800 border-zinc-600 text-white placeholder:text-zinc-500"
                  />
                  <Button variant="outline" onClick={addKnowledgeUrl} className="border-zinc-600 text-zinc-300 hover:bg-zinc-700">
                    <Link className="w-4 h-4 mr-1" /> Hinzufügen
                  </Button>
                </div>
              </div>
              
              {/* URLs List */}
              {chatbotSettings.knowledge_urls && chatbotSettings.knowledge_urls.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-zinc-300">Gespeicherte URLs</Label>
                  <div className="space-y-2">
                    {chatbotSettings.knowledge_urls.map((url, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-zinc-800 rounded border border-zinc-700">
                        <Globe className="w-4 h-4 text-blue-400" />
                        <span className="flex-1 text-sm truncate text-zinc-300">{url}</span>
                        <Button variant="ghost" size="sm" onClick={() => scrapeUrl(url)} disabled={scraping} className="text-zinc-400 hover:text-white hover:bg-zinc-700">
                          {scraping ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => removeKnowledgeUrl(url)} className="text-red-400 hover:text-red-300 hover:bg-zinc-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* 08/2026: Manuelles Wissen eintragen */}
              <div className="space-y-2 border border-zinc-700 rounded-lg p-3 bg-zinc-800/50">
                <Label className="text-zinc-300">✍️ Wissen manuell eintragen</Label>
                <p className="text-xs text-zinc-500">
                  Kundenfrage + Antwort (oder Freitext) eingeben — der Bot nutzt es sofort und
                  versteht auch anders formulierte Fragen.
                </p>
                <Input
                  value={manualKnowledgeTitle}
                  onChange={(e) => setManualKnowledgeTitle(e.target.value)}
                  placeholder="Titel (z.B. 'Duschen im Studio')"
                  className="bg-zinc-800 border-zinc-600 text-white"
                  data-testid="manual-knowledge-title-input"
                />
                <Textarea
                  value={manualKnowledgeContent}
                  onChange={(e) => setManualKnowledgeContent(e.target.value)}
                  placeholder={"Frage: Gibt es Duschen im Studio?\nAntwort: Ja, wir haben moderne Duschen und Umkleiden in Wetzikon."}
                  rows={4}
                  className="bg-zinc-800 border-zinc-600 text-white"
                  data-testid="manual-knowledge-content-input"
                />
                <Button
                  onClick={saveManualKnowledge}
                  disabled={savingManualKnowledge}
                  size="sm"
                  data-testid="manual-knowledge-save-btn"
                >
                  {savingManualKnowledge ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : null}
                  Wissen speichern
                </Button>
              </div>

              {/* Document Upload */}
              <div className="space-y-2">
                <Label className="text-zinc-300">Dokumente & Fotos hochladen</Label>
                <Input
                  value={documentTopic}
                  onChange={(e) => setDocumentTopic(e.target.value)}
                  placeholder="Thema/Titel (optional, z.B. 'Preisliste 2026')"
                  className="bg-zinc-800 border-zinc-600 text-white placeholder:text-zinc-500"
                  data-testid="document-topic-input"
                />
                <div className="flex gap-2">
                  <Input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleDocumentUpload}
                    accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.webp"
                    className="flex-1 bg-zinc-800 border-zinc-600 text-white file:bg-zinc-700 file:text-zinc-300 file:border-0"
                    data-testid="knowledge-file-input"
                  />
                  <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="border-zinc-600 text-zinc-300 hover:bg-zinc-700">
                    {uploading ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                    Upload
                  </Button>
                </div>
                <p className="text-xs text-zinc-500">
                  Formate: PDF, DOC, DOCX, TXT — oder 📸 Fotos (JPG, PNG, WEBP): die KI liest das
                  Bild (Preisliste, Flyer, Screenshot, Notiz) und übernimmt das Wissen automatisch.
                </p>
                {photoExtractPreview && (
                  <div className="p-2 bg-zinc-800 rounded border border-emerald-700/50 text-xs text-zinc-300" data-testid="photo-extract-preview">
                    <div className="font-semibold text-emerald-400 mb-1">📸 Von der KI aus dem Foto gelesen:</div>
                    <p className="whitespace-pre-wrap max-h-32 overflow-y-auto">{photoExtractPreview}</p>
                  </div>
                )}
              </div>
              
              {/* Documents List */}
              {chatbotDocuments && chatbotDocuments.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-zinc-300">Hochgeladene Dokumente ({chatbotDocuments.length})</Label>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {chatbotDocuments.map((doc, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-zinc-800 rounded border border-zinc-700">
                        <FileText className="w-4 h-4 text-blue-400" />
                        <span className="flex-1 text-sm truncate text-zinc-300">{doc.filename || doc.name}</span>
                        {doc.topic && (
                          <Badge variant="outline" className="text-orange-300 border-orange-600/60 shrink-0" data-testid={`document-topic-badge-${idx}`}>
                            {doc.topic}
                          </Badge>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => deleteDocument(doc.id || doc._id)} className="text-red-400 hover:text-red-300 hover:bg-zinc-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Test Chatbot */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TestTube className="text-cyan-600" />
                Chatbot testen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Geben Sie eine Test-Nachricht ein..."
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && testChatbot()}
                />
                <Button onClick={testChatbot} disabled={testingChatbot}>
                  {testingChatbot ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                </Button>
              </div>
              
              {testResponse && (
                <div className="p-4 bg-gray-50 rounded-lg border">
                  <Label className="text-sm text-gray-500 mb-2 block">Bot-Antwort:</Label>
                  <p className="text-gray-800 whitespace-pre-wrap">{testResponse}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* ============ Ernährung Tab ============ */}
        <TabsContent value="ki-ernährung" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Apple className="text-green-600" />
                Portal Ernährungs-KI
              </CardTitle>
              <CardDescription>
                KI-Assistent im Kundenportal für Mahlzeit-Analyse, Foto-Erkennung und Ernährungsberatung
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                <div>
                  <p className="font-medium text-green-900">Portal Ernährungs-KI aktivieren</p>
                  <p className="text-xs text-green-700">KI-Assistent im Kundenportal für Ernährungsberatung und Foto-Analyse</p>
                </div>
                <Switch
                  checked={aiSettings.nutrition_ai_enabled}
                  onCheckedChange={(checked) => setAiSettings({ ...aiSettings, nutrition_ai_enabled: checked })}
                  data-testid="nutrition-ai-toggle"
                />
              </div>
              
              {aiSettings.nutrition_ai_enabled && (
                <>
                  <Card className="border-indigo-200" data-testid="nutrition-model-mapping-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Modell-Zuordnung Ernährung</CardTitle>
                      <CardDescription>
                        Für jede Ernährungsfunktion kannst du direkt das KI-Modell im Tab „KI-Modelle“ öffnen.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="grid gap-2">
                      {[
                        { label: 'Ernährungs-KI (Text/Analyse)', serviceId: 'nutrition_ai' },
                        { label: 'Foto-Analyse', serviceId: 'photo_analysis' },
                        { label: 'Sprach-Analyse Ernährung', serviceId: 'voice_analysis' },
                        { label: 'AI-Quick-Create (Lebensmittel)', serviceId: 'nutrition_quick_create' },
                        { label: 'Trainingsaktivität-Suche', serviceId: 'training_assistant' },
                        { label: 'Mobile App KI', serviceId: 'mobile_ai' },
                        { label: 'KI-Validierung Ernährung', serviceId: 'validation_ai' },
                      ].map((item) => (
                        <div
                          key={item.serviceId}
                          className="flex items-center justify-between rounded-lg border bg-indigo-50/50 px-3 py-2"
                          data-testid={`nutrition-model-mapping-item-${item.serviceId}`}
                        >
                          <span className="text-sm text-zinc-800">{item.label}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openModelService(item.serviceId)}
                            data-testid={`nutrition-model-mapping-jump-${item.serviceId}`}
                          >
                            Modell öffnen
                          </Button>
                        </div>
                      ))}
                      <div className="flex items-center justify-between rounded-lg border bg-emerald-50 px-3 py-2" data-testid="nutrition-model-mapping-item-product-score">
                        <span className="text-sm text-zinc-800">Produkt-Score (Yuka-ähnlich)</span>
                        <Badge variant="secondary" data-testid="nutrition-model-mapping-product-score-note">Kein KI-Modell (regelbasiert)</Badge>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Bot Personality Settings - Integrated */}
                  <Card className="border-emerald-200">
                    <CardHeader className="bg-emerald-50/50 pb-3">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Bot className="h-5 w-5 text-emerald-600" />
                        Portal Ernährungs-Bot Persönlichkeit
                      </CardTitle>
                      <CardDescription>
                        Persönlichkeit des KI-Assistenten im Kundenportal (Ernährungsbereich)
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4 pt-4">
                      {/* Bot Name */}
                      <div>
                        <Label>Name des Assistenten</Label>
                        <Input
                          value={nutritionBotSettings.name}
                          onChange={(e) => setNutritionBotSettings({ ...nutritionBotSettings, name: e.target.value })}
                          placeholder="KI-Ernährungsberater"
                          className="mt-1"
                        />
                      </div>
                      
                      {/* Role Description */}
                      <div>
                        <Label className="flex items-center gap-2">
                          Rolle & Aufgaben
                          <Badge variant="outline" className="text-xs">System Prompt</Badge>
                        </Label>
                        <p className="text-xs text-gray-500 mb-2">
                          Was soll der Bot tun? (z.B. Rezepte vorschlagen, Zutaten erkennen)
                        </p>
                        <Textarea
                          value={nutritionBotSettings.role_description}
                          onChange={(e) => setNutritionBotSettings({ ...nutritionBotSettings, role_description: e.target.value })}
                          rows={5}
                          className="font-mono text-sm"
                          placeholder="Du bist ein freundlicher Ernährungsberater-Assistent..."
                        />
                      </div>

                      {/* Style Description */}
                      <div>
                        <Label>Stil & Sprache</Label>
                        <p className="text-xs text-gray-500 mb-2">
                          Wie soll der Bot kommunizieren?
                        </p>
                        <Textarea
                          value={nutritionBotSettings.style_description}
                          onChange={(e) => setNutritionBotSettings({ ...nutritionBotSettings, style_description: e.target.value })}
                          rows={3}
                          className="font-mono text-sm"
                          placeholder="Antworte immer auf Deutsch und sei freundlich..."
                        />
                      </div>

                      {/* Boundaries */}
                      <div>
                        <Label className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-red-500" />
                          Grenzen & Tabus
                        </Label>
                        <p className="text-xs text-gray-500 mb-2">
                          Was darf der Bot NICHT tun oder sagen?
                        </p>
                        <Textarea
                          value={nutritionBotSettings.boundaries}
                          onChange={(e) => setNutritionBotSettings({ ...nutritionBotSettings, boundaries: e.target.value })}
                          rows={2}
                          className="font-mono text-sm"
                          placeholder="Keine medizinischen Ratschläge geben..."
                        />
                      </div>
                      
                      {/* Chat History Settings */}
                      <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                        <div>
                          <Label>Speicherdauer Chat-Verlauf</Label>
                          <Select
                            value={String(nutritionBotSettings.chat_history_hours)}
                            onValueChange={(value) => setNutritionBotSettings({ ...nutritionBotSettings, chat_history_hours: parseInt(value) })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">1 Stunde</SelectItem>
                              <SelectItem value="6">6 Stunden</SelectItem>
                              <SelectItem value="12">12 Stunden</SelectItem>
                              <SelectItem value="24">24 Stunden</SelectItem>
                              <SelectItem value="48">48 Stunden</SelectItem>
                              <SelectItem value="168">7 Tage</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label>Max. Nachrichten/Session</Label>
                          <Select
                            value={String(nutritionBotSettings.max_messages_per_session)}
                            onValueChange={(value) => setNutritionBotSettings({ ...nutritionBotSettings, max_messages_per_session: parseInt(value) })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="20">20 Nachrichten</SelectItem>
                              <SelectItem value="50">50 Nachrichten</SelectItem>
                              <SelectItem value="100">100 Nachrichten</SelectItem>
                              <SelectItem value="200">200 Nachrichten</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      {/* Save Button */}
                      <div className="flex justify-end pt-2">
                        <Button 
                          onClick={saveNutritionBotSettings} 
                          disabled={savingNutritionBot}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          {savingNutritionBot ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Save className="h-4 w-4 mr-2" />
                          )}
                          Bot-Einstellungen speichern
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-3">
                      <Sparkles className="text-amber-600" />
                      <div>
                        <p className="font-medium text-amber-900">AI-Quick-Create aktivieren</p>
                        <p className="text-xs text-amber-700">Fehlende Lebensmittel werden automatisch erstellt</p>
                      </div>
                    </div>
                    <Switch
                      checked={aiSettings.nutrition_quick_create_enabled}
                      onCheckedChange={(checked) => setAiSettings({ ...aiSettings, nutrition_quick_create_enabled: checked })}
                      data-testid="nutrition-quick-create-toggle"
                    />
                  </div>
                  <p className="text-xs text-gray-500 -mt-3">
                    Modell/Provider wählen Sie im Tab <strong>„KI-Modelle“</strong> unter „AI-Quick-Create (Lebensmittel)“.
                  </p>

                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-3">
                      <Camera className="text-blue-600" />
                      <div>
                        <p className="font-medium text-blue-900">Foto-Analyse aktivieren</p>
                        <p className="text-xs text-blue-700">Mahlzeiten per Foto erkennen</p>
                      </div>
                    </div>
                    <Switch
                      checked={aiSettings.nutrition_photo_analysis}
                      onCheckedChange={(checked) => setAiSettings({ ...aiSettings, nutrition_photo_analysis: checked })}
                      data-testid="photo-analysis-toggle"
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center gap-3">
                      <Smartphone className="text-purple-600" />
                      <div>
                        <p className="font-medium text-purple-900">Mobile App KI</p>
                        <p className="text-xs text-purple-700">KI-Features in der App aktivieren</p>
                      </div>
                    </div>
                    <Switch
                      checked={aiSettings.mobile_ai_enabled}
                      onCheckedChange={(checked) => setAiSettings({ ...aiSettings, mobile_ai_enabled: checked })}
                      data-testid="mobile-ai-toggle"
                    />
                  </div>

                  <Card className="border-emerald-200" data-testid="product-scoring-settings-card">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-emerald-600" />
                        Produkt-Score (Yuka-ähnlich)
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Bewertet Produkte mit 0–100 Score und Ampel (Nährwerte, Zusatzstoffe, Bio).
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200" data-testid="product-scoring-enable-row">
                        <div>
                          <p className="font-medium text-emerald-900">Produkt-Score aktivieren</p>
                          <p className="text-xs text-emerald-700">Kann jederzeit ein-/ausgeschaltet werden.</p>
                        </div>
                        <Switch
                          checked={!!aiSettings.product_scoring_enabled}
                          onCheckedChange={(checked) => setAiSettings({ ...aiSettings, product_scoring_enabled: checked })}
                          data-testid="product-scoring-enabled-toggle"
                        />
                      </div>

                      {aiSettings.product_scoring_enabled && (
                        <>
                          <div className="grid md:grid-cols-2 gap-3" data-testid="product-scoring-component-toggles">
                            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <div>
                                <p className="text-sm font-medium text-blue-900">Nährwert-Komponente</p>
                              </div>
                              <Switch
                                checked={!!aiSettings.product_scoring_nutrition_enabled}
                                onCheckedChange={(checked) => setAiSettings({ ...aiSettings, product_scoring_nutrition_enabled: checked })}
                                data-testid="product-scoring-nutrition-toggle"
                              />
                            </div>
                            <div className="flex items-center justify-between p-3 bg-violet-50 rounded-lg border border-violet-200">
                              <div>
                                <p className="text-sm font-medium text-violet-900">Zusatzstoff-Komponente</p>
                              </div>
                              <Switch
                                checked={!!aiSettings.product_scoring_additives_enabled}
                                onCheckedChange={(checked) => setAiSettings({ ...aiSettings, product_scoring_additives_enabled: checked })}
                                data-testid="product-scoring-additives-toggle"
                              />
                            </div>
                            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200 md:col-span-2">
                              <div className="flex items-center gap-2">
                                <Leaf className="w-4 h-4 text-green-700" />
                                <div>
                                  <p className="text-sm font-medium text-green-900">Bio-Komponente</p>
                                  <p className="text-xs text-green-700">Unbekannt-Regel kann separat gewählt werden</p>
                                </div>
                              </div>
                              <Switch
                                checked={!!aiSettings.product_scoring_bio_enabled}
                                onCheckedChange={(checked) => setAiSettings({ ...aiSettings, product_scoring_bio_enabled: checked })}
                                data-testid="product-scoring-bio-toggle"
                              />
                            </div>
                          </div>

                          <div className="grid md:grid-cols-3 gap-3" data-testid="product-scoring-weight-inputs">
                            <div>
                              <Label htmlFor="product-score-weight-nutrition" className="text-xs">Gewichtung Nährwerte</Label>
                              <Input
                                id="product-score-weight-nutrition"
                                type="number"
                                min={0}
                                value={aiSettings.product_scoring_weight_nutrition ?? 60}
                                onChange={(e) => setAiSettings({ ...aiSettings, product_scoring_weight_nutrition: Number(e.target.value || 0) })}
                                className="mt-1"
                                data-testid="product-scoring-weight-nutrition-input"
                              />
                            </div>
                            <div>
                              <Label htmlFor="product-score-weight-additives" className="text-xs">Gewichtung Zusatzstoffe</Label>
                              <Input
                                id="product-score-weight-additives"
                                type="number"
                                min={0}
                                value={aiSettings.product_scoring_weight_additives ?? 30}
                                onChange={(e) => setAiSettings({ ...aiSettings, product_scoring_weight_additives: Number(e.target.value || 0) })}
                                className="mt-1"
                                data-testid="product-scoring-weight-additives-input"
                              />
                            </div>
                            <div>
                              <Label htmlFor="product-score-weight-bio" className="text-xs">Gewichtung Bio</Label>
                              <Input
                                id="product-score-weight-bio"
                                type="number"
                                min={0}
                                value={aiSettings.product_scoring_weight_bio ?? 10}
                                onChange={(e) => setAiSettings({ ...aiSettings, product_scoring_weight_bio: Number(e.target.value || 0) })}
                                className="mt-1"
                                data-testid="product-scoring-weight-bio-input"
                              />
                            </div>
                          </div>

                          <div className="p-3 bg-zinc-50 rounded-lg border" data-testid="product-scoring-bio-policy-card">
                            <Label className="text-xs mb-2 flex items-center gap-2">
                              <SlidersHorizontal className="w-3.5 h-3.5" />
                              Bio bei unbekannten Daten
                            </Label>
                            <Select
                              value={aiSettings.product_scoring_bio_unknown_policy || 'only_when_known'}
                              onValueChange={(value) => setAiSettings({ ...aiSettings, product_scoring_bio_unknown_policy: value })}
                            >
                              <SelectTrigger className="w-full" data-testid="product-scoring-bio-policy-select">
                                <SelectValue placeholder="Regel wählen" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="only_when_known">Nur bewerten, wenn Bio-Info eindeutig vorhanden</SelectItem>
                                <SelectItem value="neutral_when_unknown">Bei unbekannt neutral (50 Punkte)</SelectItem>
                                <SelectItem value="zero_when_unknown">Bei unbekannt 0 Punkte</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="grid md:grid-cols-2 gap-3" data-testid="product-scoring-visibility-toggles">
                            <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                              <p className="text-sm text-zinc-800">In Suche anzeigen</p>
                              <Switch
                                checked={!!aiSettings.product_scoring_show_search}
                                onCheckedChange={(checked) => setAiSettings({ ...aiSettings, product_scoring_show_search: checked })}
                                data-testid="product-scoring-show-search-toggle"
                              />
                            </div>
                            <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                              <p className="text-sm text-zinc-800">Im Barcode-Scan anzeigen</p>
                              <Switch
                                checked={!!aiSettings.product_scoring_show_barcode}
                                onCheckedChange={(checked) => setAiSettings({ ...aiSettings, product_scoring_show_barcode: checked })}
                                data-testid="product-scoring-show-barcode-toggle"
                              />
                            </div>
                            <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                              <p className="text-sm text-zinc-800">Im Produkt-Detail anzeigen</p>
                              <Switch
                                checked={!!aiSettings.product_scoring_show_product_detail}
                                onCheckedChange={(checked) => setAiSettings({ ...aiSettings, product_scoring_show_product_detail: checked })}
                                data-testid="product-scoring-show-product-detail-toggle"
                              />
                            </div>
                            <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                              <p className="text-sm text-zinc-800">In Favoriten & Zuletzt anzeigen</p>
                              <Switch
                                checked={!!aiSettings.product_scoring_show_favorites_recent}
                                onCheckedChange={(checked) => setAiSettings({ ...aiSettings, product_scoring_show_favorites_recent: checked })}
                                data-testid="product-scoring-show-favorites-recent-toggle"
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>

                  {/* KI-Prompts für Kalorien-Berechnung */}
                  <Card className="border-orange-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-orange-600" />
                        KI-Prompts für Berechnungen
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Diese Prompts steuern, wie die KI Kalorien und Nachbrenn-Effekte berechnet
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label className="mb-2 block text-sm font-medium">KI-Prompt für Kalorien-Berechnung</Label>
                        <Textarea
                          value={aiSettings.ai_calorie_prompt || `Du bist ein Ernährungsexperte. Berechne den täglichen Kalorienbedarf basierend auf:
- Grundumsatz (BMR) nach Mifflin-St Jeor Formel
- Aktivitätslevel und Trainings
- Körperzusammensetzung (Fettanteil, Muskelmasse)
- Ziel (Aufbau, Definition, Erhaltung)

Berücksichtige:
- Menschen mit weniger Körperfett haben einen höheren Grundumsatz
- Muskelmasse verbrennt mehr Kalorien als Fettgewebe
- Der Nachbrenn-Effekt (EPOC) nach intensivem Training

Gib eine präzise Kalorienempfehlung mit kurzer Begründung.`}
                          onChange={(e) => setAiSettings({ ...aiSettings, ai_calorie_prompt: e.target.value })}
                          className="min-h-[150px] font-mono text-xs"
                          placeholder="Anweisungen für die KI zur Kalorien-Berechnung..."
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Wird verwendet, um den täglichen Kalorienbedarf zu berechnen
                        </p>
                      </div>

                      <div>
                        <Label className="mb-2 block text-sm font-medium">KI-Prompt für Nachbrenn-Effekt (EPOC)</Label>
                        <Textarea
                          value={aiSettings.ai_afterburn_prompt || `Berechne den Nachbrenn-Effekt (EPOC) basierend auf:
- Trainingsart und Intensität
- Trainingsdauer
- Körpergewicht und Fitness-Level

Der Nachbrenn-Effekt beträgt typischerweise:
- Leichtes Training: 5% der verbrannten Kalorien
- Moderates Training: 10%
- Intensives Training: 15%
- HIIT: 20%

Dieser Effekt wirkt bis zu 24 Stunden nach dem Training.`}
                          onChange={(e) => setAiSettings({ ...aiSettings, ai_afterburn_prompt: e.target.value })}
                          className="min-h-[120px] font-mono text-xs"
                          placeholder="Anweisungen für die KI zur Nachbrenn-Berechnung..."
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Wird verwendet, um den Nachbrenn-Effekt (EPOC) nach dem Training zu berechnen
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="p-4 bg-gray-50 rounded-lg border">
                    <h4 className="font-medium mb-3">Verfügbare KI-Features:</h4>
                    <ul className="text-sm text-gray-600 space-y-2">
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600" />
                        Mahlzeit-Foto-Analyse (Kalorien, Makros)
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600" />
                        Ernährungs-Empfehlungen
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600" />
                        Lebensmittel-Erkennung via Barcode
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600" />
                        Tägliche Nährwert-Zusammenfassung
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600" />
                        KI-Kalorien-Berechnung mit Körperdaten
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600" />
                        Nachbrenn-Effekt (EPOC) Berechnung
                      </li>
                    </ul>
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm text-gray-600">
                        Verwendet: <span className="font-medium">{getCurrentProviderName()}</span>
                      </p>
                    </div>
                  </div>

                  {/* Link to Nutrition Calculation Settings (ohne KI-Prompts) */}
                  <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <BarChart3 className="w-6 h-6 text-orange-600" />
                        <div>
                          <p className="font-medium text-orange-900">Kalorien-Berechnungs-Parameter</p>
                          <p className="text-xs text-orange-700">BMR-Formel, Aktivitätslevel, Körperdaten, Nachbrenn-% Werte</p>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        className="border-orange-300 hover:bg-orange-100"
                        onClick={() => window.location.href = '/settings/advanced?tab=nutrition'}
                      >
                        <ArrowRight className="w-4 h-4 mr-2" />
                        Öffnen
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* ============ Firebase Tab ============ */}
        <TabsContent value="firebase" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="text-orange-600" />
                Firebase Cloud Messaging (FCM)
              </CardTitle>
              <CardDescription>
                Konfigurieren Sie Firebase für Push-Benachrichtigungen an die Mobile App
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Status Banner */}
              {firebaseSettings.configured ? (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-3">
                    <Check className="w-6 h-6 text-green-600" />
                    <div>
                      <p className="font-medium text-green-900">Firebase konfiguriert</p>
                      <p className="text-sm text-green-700">
                        Projekt: <span className="font-mono">{firebaseSettings.project_id}</span>
                      </p>
                      <p className="text-xs text-green-600 mt-1">
                        Service Account: {firebaseSettings.client_email}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-900">Firebase nicht konfiguriert</p>
                      <p className="text-sm text-amber-700">
                        Push-Benachrichtigungen werden erst funktionieren, nachdem Sie Firebase konfiguriert haben.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Instructions */}
              <div className="space-y-3">
                <h3 className="font-semibold text-lg border-b pb-2">Einrichtungs-Anleitung</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="flex-shrink-0 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                    <div>
                      <p className="font-medium">Firebase Console öffnen</p>
                      <a href="https://console.firebase.google.com/" target="_blank" rel="noopener noreferrer" 
                         className="text-blue-600 hover:underline">
                        https://console.firebase.google.com/ ↗
                      </a>
                    </div>
                  </div>
                  <div className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="flex-shrink-0 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                    <div>
                      <p className="font-medium">Neues Projekt erstellen</p>
                      <p className="text-gray-600">Klicken Sie auf "Projekt hinzufügen" und folgen Sie den Anweisungen</p>
                    </div>
                  </div>
                  <div className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="flex-shrink-0 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                    <div>
                      <p className="font-medium">Service Account Key erstellen</p>
                      <p className="text-gray-600">
                        Projekteinstellungen → Dienstkonten → "Neuen privaten Schlüssel generieren"
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="flex-shrink-0 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                    <div>
                      <p className="font-medium">JSON-Datei hier einfügen</p>
                      <p className="text-gray-600">Kopieren Sie den gesamten Inhalt der heruntergeladenen JSON-Datei</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* JSON Input */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Key className="w-4 h-4 text-orange-600" />
                  Service Account JSON
                </Label>

                {/* iOS/Android Setup-Hinweis für FCM-Versand an Mobile-App */}
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-900 space-y-1.5">
                  <p className="font-semibold flex items-center gap-1">📱 Mobile App (iOS + Android) Setup</p>
                  <p>Damit die Flutter-App Push-Benachrichtigungen empfängt, musst du zusätzlich <strong>einmalig</strong>:</p>
                  <ol className="list-decimal ml-4 space-y-0.5">
                    <li>Im <a href="https://console.firebase.google.com" target="_blank" rel="noreferrer" className="underline font-medium">Firebase Console</a> die iOS-App (<code>com.strongfit.app</code>) und Android-App registrieren (falls noch nicht).</li>
                    <li><strong>iOS</strong>: <code>GoogleService-Info.plist</code> herunterladen und in <code>/ios/Runner/</code> ablegen (im Codebase, wird im Codemagic-Build eingepackt).</li>
                    <li><strong>Android</strong>: <code>google-services.json</code> herunterladen und in <code>/android/app/</code> ablegen.</li>
                    <li><strong>APNs Auth Key</strong> (<code>.p8</code> Datei) aus dem Apple Developer Portal herunterladen und in Firebase Console → Project Settings → Cloud Messaging → Apple app configuration hochladen (mit Key ID + Team ID).</li>
                    <li>Codemagic-Build neu starten — fertig!</li>
                  </ol>
                  <p className="italic mt-1">Server-seitige Zugangsdaten (das JSON unten) sind in unserem Admin konfigurierbar. APNs-Konfiguration muss direkt in Firebase erfolgen.</p>
                </div>

                <Textarea
                  placeholder='{"type": "service_account", "project_id": "...", ...}'
                  value={firebaseSettings.service_account_json || ''}
                  onChange={(e) => setFirebaseSettings({ ...firebaseSettings, service_account_json: e.target.value })}
                  rows={10}
                  className="font-mono text-xs"
                  data-testid="firebase-json-input"
                />
                <p className="text-xs text-gray-500">
                  Fügen Sie den gesamten Inhalt der Firebase Service Account JSON-Datei ein.
                  Die Daten werden sicher verschlüsselt gespeichert.
                </p>
              </div>

              {/* Test Result */}
              {firebaseTestResult && (
                <div className={`p-4 rounded-lg border ${
                  firebaseTestResult.success 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-center gap-2">
                    {firebaseTestResult.success ? (
                      <Check className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    )}
                    <span className={firebaseTestResult.success ? 'text-green-800' : 'text-red-800'}>
                      {firebaseTestResult.message}
                    </span>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button 
                  onClick={saveFirebaseSettings}
                  disabled={savingFirebase || !firebaseSettings.service_account_json}
                  data-testid="save-firebase-btn"
                >
                  {savingFirebase ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {savingFirebase ? 'Speichern...' : 'Speichern'}
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={testFirebaseConnection}
                  disabled={testingFirebase || !firebaseSettings.configured}
                  data-testid="test-firebase-btn"
                >
                  {testingFirebase ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  {testingFirebase ? 'Teste...' : 'Verbindung testen'}
                </Button>
              </div>

              {/* Features Info */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-900 mb-2">Nach der Konfiguration verfügbar:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-blue-800">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    <span>Termin-Erinnerungen (24h & 1h)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    <span>Inaktivitäts-Erinnerungen</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    <span>Ernährungs-Erinnerungen</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    <span>Trainings-Motivation</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    <span>Wasser-Erinnerungen</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    <span>Wöchentliche Reports</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* ============ Email SMTP Tab ============ */}
        <TabsContent value="email-smtp" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="text-blue-600" />
                SMTP Email Server
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div>
                  <p className="font-medium text-blue-900">Email-Versand aktivieren</p>
                </div>
                <Switch
                  checked={smtpSettings.enabled}
                  onCheckedChange={(checked) => setSmtpSettings({ ...smtpSettings, enabled: checked })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>SMTP Host</Label>
                  <Input
                    placeholder="smtp.gmail.com"
                    value={smtpSettings.smtp_host || ''}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_host: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>SMTP Port</Label>
                  <Input
                    type="number"
                    placeholder="587"
                    value={smtpSettings.smtp_port || 587}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_port: parseInt(e.target.value) })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>SMTP Benutzername</Label>
                <Input
                  type="email"
                  placeholder="your-email@gmail.com"
                  value={smtpSettings.smtp_username || ''}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_username: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>SMTP Passwort / App-Passwort</Label>
                <Input
                  type="password"
                  placeholder="Ihr App-Passwort"
                  value={smtpSettings.smtp_password || ''}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_password: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Absender Email</Label>
                  <Input
                    type="email"
                    placeholder="noreply@yourstudio.com"
                    value={smtpSettings.smtp_from_email || ''}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_from_email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Absender Name</Label>
                  <Input
                    placeholder="StrongFit Studio"
                    value={smtpSettings.smtp_from_name || ''}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_from_name: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-3 mt-2 border-t">
                <Button type="button" variant="outline" onClick={handleTestSmtp} disabled={testingSmtp} data-testid="smtp-test-btn">
                  {testingSmtp ? 'Sende…' : 'Test-Mail senden'}
                </Button>
                <span className="text-xs text-gray-500">Sendet eine Test-Mail an deine Admin-Adresse. Zuerst speichern.</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* ============ WhatsApp API Tab ============ */}
        <TabsContent value="whatsapp-api" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="text-green-600" />
                WhatsApp Business API
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                <div>
                  <p className="font-medium text-green-900">WhatsApp Integration aktivieren</p>
                </div>
                <Switch
                  checked={whatsappSettings.enabled}
                  onCheckedChange={(checked) => setWhatsappSettings({ ...whatsappSettings, enabled: checked })}
                />
              </div>
              <div className="space-y-2">
                <Label>WhatsApp Phone ID</Label>
                <Input
                  placeholder="123456789012345"
                  value={whatsappSettings.phone_id || ''}
                  onChange={(e) => setWhatsappSettings({ ...whatsappSettings, phone_id: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Access Token</Label>
                <Input
                  type="password"
                  placeholder="EAAxxxxxxx..."
                  value={whatsappSettings.access_token || ''}
                  onChange={(e) => setWhatsappSettings({ ...whatsappSettings, access_token: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Business Account ID</Label>
                <Input
                  placeholder="123456789012345"
                  value={whatsappSettings.business_account_id || ''}
                  onChange={(e) => setWhatsappSettings({ ...whatsappSettings, business_account_id: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Meta App ID</Label>
                  <Input
                    data-testid="whatsapp-meta-app-id-input"
                    placeholder="123456789012345"
                    value={whatsappSettings.meta_app_id || ''}
                    onChange={(e) => setWhatsappSettings({ ...whatsappSettings, meta_app_id: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Meta App-Secret</Label>
                  <Input
                    data-testid="whatsapp-meta-app-secret-input"
                    type="password"
                    placeholder={whatsappSettings.meta_app_secret === '***SET***' ? 'Gespeichert (***SET***)' : '32-stelliger Geheimcode'}
                    value={whatsappSettings.meta_app_secret === '***SET***' ? '' : (whatsappSettings.meta_app_secret || '')}
                    onChange={(e) => setWhatsappSettings({ ...whatsappSettings, meta_app_secret: e.target.value })}
                  />
                  <p className="text-xs text-gray-500">
                    Sichert den Webhook ab (Signaturprüfung). Muss zur App gehören, deren Webhook auf diese Umgebung zeigt.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Verify Token</Label>
                  <Input
                    placeholder="your_verify_token"
                    value={whatsappSettings.verify_token || ''}
                    onChange={(e) => setWhatsappSettings({ ...whatsappSettings, verify_token: e.target.value })}
                  />
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border">
                <h4 className="font-medium mb-2">Webhook URL</h4>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-gray-200 px-2 py-1 rounded text-sm">
                    {window.location.origin}/api/whatsapp/webhook
                  </code>
                  <Button variant="outline" size="sm" onClick={() => copyWebhookUrl(`${window.location.origin}/api/whatsapp/webhook`)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex-1">
                  <p className="font-medium text-purple-900">KI Auto-Antwort für WhatsApp</p>
                  <p className="text-xs text-purple-700 mt-1">
                    Die KI-Antworten werden über den <strong>"KI-Chatbot"</strong> Tab gesteuert.
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-purple-300 text-purple-700 hover:bg-purple-100"
                  onClick={() => setActiveTab('kommunikation')}
                >
                  Zum Kommunikation-Tab →
                </Button>
              </div>

              <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="font-medium text-blue-900">Sprachnachrichten-Transkription</p>
                    <p className="text-xs text-blue-700">
                      Wird im <strong>"KI-Modelle"</strong> Tab unter "Audio & Transkription" konfiguriert.
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                    onClick={() => setActiveTab('ki-modelle')}
                  >
                    Zu KI-Modelle →
                  </Button>
                </div>
              </div>

              <div className="space-y-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="font-medium text-amber-900">Win-Back WhatsApp Vorlagen</p>
                    <p className="text-xs text-amber-700">
                      Automatische Nachrichten an Kunden ohne Buchung (mit Rabattcode). Konfiguration unter <strong>Automation → Win-Back Flow → Vorlagen</strong>.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* ============ Payments Tab ============ */}
        <TabsContent value="zahlungen" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="text-green-600" />
                Payrexx Integration (TWINT)
              </CardTitle>
              <CardDescription>
                TWINT, Kreditkarten, PostFinance - Schweizer Zahlungsanbieter
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Payrexx Instance Name</Label>
                <Input
                  type="text"
                  placeholder="your_instance_name"
                  value={paymentSettings.payrexx_instance_name || ''}
                  onChange={(e) => setPaymentSettings({ ...paymentSettings, payrexx_instance_name: e.target.value })}
                  data-testid="payrexx-instance"
                />
                <p className="text-xs text-gray-500">Ihr Subdomain-Name bei Payrexx (z.B. "strongfit")</p>
              </div>
              <div className="space-y-2">
                <Label>Payrexx API Secret</Label>
                <Input
                  type="password"
                  placeholder="Ihr API Secret"
                  value={paymentSettings.payrexx_api_secret || ''}
                  onChange={(e) => setPaymentSettings({ ...paymentSettings, payrexx_api_secret: e.target.value })}
                  data-testid="payrexx-secret"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" onClick={handleTestPayrexx} disabled={testingPayrexx} data-testid="payrexx-test-btn" className="gap-2">
                  <CreditCard className="h-4 w-4" />
                  {testingPayrexx ? 'Teste…' : 'Verbindung testen'}
                </Button>
                <span className="text-xs text-gray-500">Zuerst speichern, dann testen.</span>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Payrexx Webhook Secret
                  {paymentSettings.payrexx_webhook_secret ? (
                    <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-medium" data-testid="payrexx-webhook-badge-own">
                      ✓ Eigenes Secret gesetzt
                    </span>
                  ) : webhookTokenConfigured ? (
                    <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-medium" data-testid="payrexx-webhook-badge-server">
                      ✓ Server-Token aktiv
                    </span>
                  ) : (
                    <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-medium" data-testid="payrexx-webhook-badge-required">
                      ERFORDERLICH
                    </span>
                  )}
                </Label>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    placeholder="Webhook Secret (z.B. via 'Generieren'-Button)"
                    value={paymentSettings.payrexx_webhook_secret || ''}
                    onChange={(e) => setPaymentSettings({ ...paymentSettings, payrexx_webhook_secret: e.target.value })}
                    data-testid="payrexx-webhook"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="shrink-0"
                    data-testid="payrexx-webhook-generate-btn"
                    onClick={() => {
                      const bytes = new Uint8Array(24);
                      window.crypto.getRandomValues(bytes);
                      const secret = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
                      setPaymentSettings(prev => ({ ...prev, payrexx_webhook_secret: secret }));
                      toast.success('Neues Webhook Secret generiert — jetzt speichern und die URL unten in Payrexx eintragen');
                    }}
                  >
                    Generieren
                  </Button>
                </div>
                <p className="text-[11px] text-gray-500 leading-relaxed">
                  Sicherheits-Token (Shared Secret) zur Verifizierung von Payrexx-Webhooks.
                  Mit "Generieren" einen Zufallswert erstellen, speichern und GENAU denselben Wert
                  unten in der Payrexx-Webhook-URL als <code>?token=…</code> eintragen. Ohne Match werden
                  alle Webhooks mit 401 abgelehnt (SEC-002 Fail-Closed).
                </p>
              </div>
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h4 className="font-medium mb-2 text-amber-900">Webhook URL (im Payrexx-Dashboard hinterlegen)</h4>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white px-2 py-1 rounded text-xs overflow-x-auto break-all border border-amber-200">
                    {window.location.origin}/api/payrexx/webhooks/payrexx?token={paymentSettings.payrexx_webhook_secret || '<DEIN-WEBHOOK-SECRET-EINFÜGEN>'}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!paymentSettings.payrexx_webhook_secret}
                    onClick={() => copyWebhookUrl(`${window.location.origin}/api/payrexx/webhooks/payrexx?token=${paymentSettings.payrexx_webhook_secret}`)}
                    data-testid="copy-payrexx-webhook-url"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-amber-800 mt-2 leading-relaxed">
                  ⚠️ Trage <strong>exakt diese URL</strong> in deinem Payrexx-Dashboard unter
                  <em> Einstellungen → API → Webhooks</em> ein. Speichere zuerst hier oben dein
                  Webhook Secret, danach erscheint hier die vollständige URL zum Kopieren.
                </p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" />
                  Payrexx Einrichtung
                </h4>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li><a href="https://payrexx.com" target="_blank" rel="noopener noreferrer" className="hover:underline">Payrexx Konto erstellen</a></li>
                  <li>API-Zugang unter Einstellungen aktivieren</li>
                  <li>Instance Name und API Secret hier eintragen</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ Mobile App Tab (moved to /mobile-app) ============ */}
        {/* Mobile-App-Einstellungen wurden komplett nach /mobile-app verschoben */}
      </Tabs>
    </div>
  );
};

export default AIApiSettings;
